import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  Treemap,
  Tooltip,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

const CORR_CSV_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/correlation_matrix.csv";

const VOLS_JSON_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/vols.json";

const EXPECTED_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/expected_returns.json";

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const DIVIDENDS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/dividends.json";

const SECTORS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/sectors.json";

const STOCK_MACRO_EXPOSURES_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_macro_exposures.json";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const MACRO_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/macro_dimension_scores.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/macro_dimension_scores.json",
];

const RISK_FREE_RATE = 0.04;
const TRADING_DAYS = 252;

              const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  fontWeight: 800,
};

const title = {
  margin: 0,
  fontSize: 28,
  letterSpacing: "-0.04em",
  lineHeight: 1.05,
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color:"white"
};

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function normalizeTicker(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "");
}


function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function normalizeMacroExposurePayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function normalizeStockScreenerPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function scoreLowerMetricBetter(value, cheapValue, expensiveValue) {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0) return null;
  return clamp(100 * (expensiveValue - v) / (expensiveValue - cheapValue), 0, 100);
}

function scoreBetaDefensive(beta) {
  const b = Number(beta);
  if (!Number.isFinite(b)) return NaN;

  // Beta 0 = 100, beta 1 = 50, beta 2+ = 0.
  return clamp(100 - 50 * b, 0, 100);
}

function scoreVolatilityHighIsHigh(relativeVolToSpy) {
  const v = Number(relativeVolToSpy);
  if (!Number.isFinite(v)) return NaN;

  // Volatility equal to SPY = 50. Twice SPY volatility = 100.
  return clamp(v * 50, 0, 100);
}

function computeEvennessScore(weightValues) {
  const vals = (weightValues || []).map(Number).filter((v) => Number.isFinite(v) && v > 0);
  const total = vals.reduce((sum, v) => sum + v, 0);
  if (!vals.length || total <= 0) return NaN;

  const probs = vals.map((v) => v / total);
  const entropy = -probs.reduce((sum, p) => sum + p * Math.log(p), 0);
  const maxEntropy = Math.log(4);

  return maxEntropy > 0 ? clamp((entropy / maxEntropy) * 100, 0, 100) : NaN;
}

function getTickerFromAnyRow(row) {
  return normalizeTicker(row?.ticker ?? row?.Ticker ?? row?.symbol ?? row?.Symbol);
}

function getFirstNumeric(row, keys) {
  for (const key of keys) {
    const v = parseNumber(row?.[key]);
    if (Number.isFinite(v)) return v;
  }

  const normalizedLookup = new Map();
  for (const realKey of Object.keys(row || {})) {
    normalizedLookup.set(
      String(realKey).toLowerCase().replace(/[^a-z0-9]/g, ""),
      realKey
    );
  }

  for (const key of keys) {
    const normalized = String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
    const realKey = normalizedLookup.get(normalized);
    if (realKey) {
      const v = parseNumber(row?.[realKey]);
      if (Number.isFinite(v)) return v;
    }
  }

  return null;
}

function getMacroExposure(row, dimension) {
  const dim = String(dimension).toLowerCase();

  return getFirstNumeric(row, [
    `${dim}_score`,
    `${dim}_exposure`,
    `${dim}_exposure_score`,
    `${dim}_beta`,
    `${dim}_corr`,
    `${dim}_correlation`,
    `${dim}Score`,
    `${dim}Exposure`,
    `${dim}Beta`,
    `${dim}Corr`,
    `${dim}Correlation`,
  ]);
}

function normalizeExposureForChart(value, allValues) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;

  const clean = allValues.filter(Number.isFinite);
  if (!clean.length) return v;

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const maxAbs = Math.max(...clean.map((x) => Math.abs(x))) || 1;

  // If the data is percentile-like, center it so 0/50/100 becomes -1/0/+1.
  if (min >= 0 && max <= 1) return v * 2 - 1;
  if (min >= 0 && max <= 100) return v / 50 - 1;

  // If data is signed but larger than -1 to +1, normalize by max absolute value.
  if (maxAbs > 2) return v / maxAbs;

  return v;
}

function normalizeDateString(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatShortDate(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatMonthShort(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
  });
}

function formatPrice(v) {
  if (!isFiniteNumber(v)) return "—";
  return `$${v.toFixed(0)}`;
}

function parseHoldings(text) {
  const lines = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const rows = [];
  for (const line of lines) {
    const parts = line.includes(",")
      ? line.split(",").map((x) => x.trim())
      : line.split(/\s+/).map((x) => x.trim());

    if (parts.length < 2) continue;

    const ticker = normalizeTicker(parts[0]);
    const wRaw = parts.slice(1).join("").replace("%", "");
    const w = Number(wRaw);

    if (!ticker || !Number.isFinite(w)) continue;
    rows.push({ ticker, weight: w });
  }

  const map = new Map();
  for (const r of rows) {
    map.set(r.ticker, (map.get(r.ticker) || 0) + r.weight);
  }

  return Array.from(map.entries()).map(([ticker, weight]) => ({
    ticker,
    weight,
  }));
}

function parseCorrelationCSV(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) throw new Error("Correlation CSV looks empty.");

  const header = lines[0].split(",").map((s) => s.trim());
  const tickers = header.slice(1).map(normalizeTicker).filter(Boolean);

  const n = tickers.length;
  if (n === 0) throw new Error("No tickers found in correlation CSV header.");

  const indexOf = new Map();
  tickers.forEach((t, i) => indexOf.set(t, i));

  const mat = Array.from({ length: n }, () => Array(n).fill(NaN));

  for (let r = 1; r < lines.length; r++) {
    const parts = lines[r].split(",").map((s) => s.trim());
    if (parts.length < n + 1) continue;

    const rowTicker = normalizeTicker(parts[0]);
    const i = indexOf.get(rowTicker);
    if (i == null) continue;

    for (let j = 0; j < n; j++) {
      const v = Number(parts[j + 1]);
      mat[i][j] = Number.isFinite(v) ? v : NaN;
    }
  }

  return { tickers, indexOf, mat };
}

function parseDailyReturnsCSV(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) throw new Error("Daily returns CSV looks empty.");

  const header = lines[0].split(",").map((s) => s.trim());
  const tickers = header.slice(1).map(normalizeTicker).filter(Boolean);

  if (!tickers.length) throw new Error("No tickers found in daily returns CSV.");

  const rows = [];

  for (let r = 1; r < lines.length; r++) {
    const parts = lines[r].split(",").map((s) => s.trim());
    if (parts.length < 2) continue;

    const date = normalizeDateString(parts[0]);
    const values = new Map();

    for (let j = 1; j < parts.length; j++) {
      const t = tickers[j - 1];
      const v = Number(parts[j]);
      values.set(t, Number.isFinite(v) ? v : NaN);
    }

    rows.push({ date, values });
  }

  return { tickers, rows };
}

async function fetchJsonFromCandidates(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim();

      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }

      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }

      const json = JSON.parse(text);
      return { json, url };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load macro JSON.");
}

function clamp(x, lo, hi) {
  return Math.min(hi, Math.max(lo, x));
}

// function clamp01(x) {
//   return clamp(x, 0, 1);
// }

function mean(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return NaN;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function fmtPct(x, decimals = 2) {
  if (!Number.isFinite(x)) return "—";
  return (x * 100).toFixed(decimals) + "%";
}

function fmtSignedPct(x, decimals = 2) {
  if (!Number.isFinite(x)) return "—";
  return `${x > 0 ? "+" : ""}${(x * 100).toFixed(decimals)}%`;
}

function fmtNum(x, decimals = 3) {
  if (!Number.isFinite(x)) return "—";
  return Number(x).toFixed(decimals);
}

function fmtRho(x, decimals = 3) {
  if (!Number.isFinite(x)) return "—";
  return Number(x).toFixed(decimals);
}

function fmtCurrency(x, decimals = 0) {
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(x);
}

function computeRSIFromReturns(returnSeries, period = 14) {
  if (!Array.isArray(returnSeries) || returnSeries.length < period) return NaN;

  let avgGain = 0;
  let avgLoss = 0;

  for (let i = 0; i < period; i++) {
    const r = Number(returnSeries[i]);
    if (!Number.isFinite(r)) return NaN;

    const gain = Math.max(r, 0);
    const loss = Math.max(-r, 0);
    avgGain += gain;
    avgLoss += loss;
  }

  avgGain /= period;
  avgLoss /= period;

  for (let i = period; i < returnSeries.length; i++) {
    const r = Number(returnSeries[i]);
    if (!Number.isFinite(r)) return NaN;

    const gain = Math.max(r, 0);
    const loss = Math.max(-r, 0);

    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
  }

  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

// function scoreAroundOneLowerBetter(value) {
//   if (!Number.isFinite(value)) return NaN;
//   return clamp(50 + 50 * (1 - value), 0, 100);
// }

function getSignLabel(v) {
  if (!isFiniteNumber(v)) return null;
  return v >= 0 ? "pos" : "neg";
}

function getMacroRegime(row) {
  const inf = getSignLabel(row?.inflation_score);
  const gro = getSignLabel(row?.growth_score);
  const liq = getSignLabel(row?.liquidity_score);

  if (!inf || !gro || !liq) return "—";

  const key = `${inf}_${gro}_${liq}`;

  const regimeMap = {
    pos_pos_pos: "Inflationary Growth + Easy Liquidity",
    pos_pos_neg: "Inflationary Growth",
    pos_neg_pos: "Stagflation + Easy Liquidity",
    pos_neg_neg: "Stagflation",
    neg_pos_pos: "Deflationary Growth",
    neg_pos_neg: "Deflationary Growth + Tight Liquidity",
    neg_neg_pos: "Deflationary Recession",
    neg_neg_neg: "Deflationary Recession + Tight Liquidity",
  };

  return regimeMap[key] || "—";
}

function collapseMacroRegime(regime) {
  const lower = String(regime || "").toLowerCase();
  if (lower.includes("stagflation")) return "Stagflation";
  if (lower.includes("deflationary recession")) return "Deflationary Recession";
  if (lower.includes("deflationary growth")) return "Deflationary Growth";
  if (lower.includes("inflationary growth")) return "Inflationary Growth";
  return "Other";
}

function getRegimeColors(regime) {
  if (!regime || regime === "—") {
    return {
      text: "#6b7280",
      bg: "#f3f4f6",
    };
  }

  const lower = regime.toLowerCase();

  if (lower.includes("stagflation")) {
    return { text: "#b45309", bg: "#fffbeb" };
  }
  if (lower.includes("deflationary recession")) {
    return { text: "#1d4ed8", bg: "#eff6ff" };
  }
  if (lower.includes("deflationary growth")) {
    return { text: "#047857", bg: "#ecfdf5" };
  }
  if (lower.includes("inflationary growth")) {
    return { text: "#7c3aed", bg: "#f5f3ff" };
  }

  return { text: "#374151", bg: "#f3f4f6" };
}

function getRegimeStyle(regime) {
  const c = getRegimeColors(regime);
  return {
    color: c.text,
    background: c.bg,
    fontWeight: 700,
    borderRadius: 9999,
    padding: "4px 10px",
    display: "inline-block",
  };
}

function getReturnStyle(v) {
  if (!isFiniteNumber(v)) return { color: "#6b7280" };
  if (v > 0) return { color: "#15803d", fontWeight: 700 };
  if (v < 0) return { color: "#b91c1c", fontWeight: 700 };
  return { color: "#374151", fontWeight: 600 };
}

function StatCard({ label, value, sublabel, background = "#ffffff" }) {
  return (
    <div
      style={{
        border: "1px solid #f1f5f9",
        borderRadius: 12,
        padding: 10,
        background,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{sublabel}</div>
    </div>
  );
}

function TreemapNode(props) {
  const { x, y, width, height, name, weightPct, index } = props;

  if (width <= 0 || height <= 0) return null;

  const fill = `hsl(${(index * 47) % 360} 60% 55%)`;
  const showBigLabel = width > 80 && height > 54;
  const showSmallLabel = width > 54 && height > 28;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={8}
        ry={8}
        fill={fill}
        stroke="#ffffff"
        strokeWidth={2}
      />
      {showBigLabel && (
        <>
          <text
            x={x + 8}
            y={y + 20}
            fill="#ffffff"
            fontSize={13}
            fontWeight={800}
          >
            {name}
          </text>
          <text
            x={x + 8}
            y={y + 38}
            fill="rgba(255,255,255,0.92)"
            fontSize={12}
            fontWeight={600}
          >
            {Number.isFinite(weightPct) ? `${weightPct.toFixed(2)}%` : ""}
          </text>
        </>
      )}
      {!showBigLabel && showSmallLabel && (
        <text
          x={x + 8}
          y={y + 18}
          fill="#ffffff"
          fontSize={12}
          fontWeight={800}
        >
          {name}
        </text>
      )}
    </g>
  );
}

function RadarPerformanceTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {point?.metric || "Metric"}
      </div>
      <div>
        Avg Portfolio Return on Positive {point?.metric} Days:{" "}
        {isFiniteNumber(point?.avg_return) ? fmtPct(point.avg_return, 2) : "—"}
      </div>
      <div>
        Relative Strength Score:{" "}
        {isFiniteNumber(point?.score) ? `${point.score}/100` : "—"}
      </div>
      <div>Sample Days: {point?.count ?? "—"}</div>
    </div>
  );
}

function PieBreakdownTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {p?.name || "Type"}
      </div>
      <div>
        Portfolio weight:{" "}
        {isFiniteNumber(p?.value) ? `${p.value.toFixed(2)}%` : "—"}
      </div>
      <div>
        Holdings:{" "}
        {Array.isArray(p?.tickers) && p.tickers.length
          ? p.tickers.join(", ")
          : "—"}
      </div>
    </div>
  );
}

function MacroPieTooltip({ active, payload, title }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
        color: "#000",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#000" }}>
        {title}
      </div>
      <div style={{ color: "#000" }}>{p?.name || "Regime"}</div>
      <div style={{ color: "#000" }}>
        Share: {isFiniteNumber(p?.value) ? `${p.value.toFixed(1)}%` : "—"}
      </div>
      <div style={{ color: "#000" }}>Days: {p?.days ?? "—"}</div>
    </div>
  );
}

function MacroPieLabel(props) {
  const { cx, cy, midAngle, outerRadius, name, percent } = props;

  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#000"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function getFirstValid(arr) {
  for (const v of arr) {
    if (isFiniteNumber(v)) return v;
  }
  return null;
}

function getLastValid(arr) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (isFiniteNumber(arr[i])) return arr[i];
  }
  return null;
}

function buildBackwardSeriesFromEnd(rows, endValue, returnField) {
  if (!rows.length || !isFiniteNumber(endValue)) return rows.map(() => null);

  const vals = new Array(rows.length).fill(null);
  vals[rows.length - 1] = endValue;

  for (let i = rows.length - 2; i >= 0; i--) {
    const nextRet = rows[i + 1]?.[returnField];
    const nextVal = vals[i + 1];
    const nextRetDecimal = isFiniteNumber(nextRet) ? nextRet / 100 : null;

    if (
      isFiniteNumber(nextVal) &&
      isFiniteNumber(nextRetDecimal) &&
      1 + nextRetDecimal !== 0
    ) {
      vals[i] = nextVal / (1 + nextRetDecimal);
    } else {
      vals[i] = null;
    }
  }

  return vals;
}

function buildForwardSeriesFromStart(rows, startValue, returnField) {
  if (!rows.length || !isFiniteNumber(startValue)) return rows.map(() => null);

  const vals = new Array(rows.length).fill(null);
  vals[0] = startValue;

  for (let i = 1; i < rows.length; i++) {
    const ret = rows[i]?.[returnField];
    const prevVal = vals[i - 1];
    const retDecimal = isFiniteNumber(ret) ? ret / 100 : null;

    if (isFiniteNumber(prevVal) && isFiniteNumber(retDecimal)) {
      vals[i] = prevVal * (1 + retDecimal);
    } else {
      vals[i] = null;
    }
  }

  return vals;
}

function getAverageReturnForRegimeFamily(rows, familyKey) {
  const filtered = rows.filter((r) => {
    const regime = String(r?.macro_regime || "").toLowerCase();
    return regime.includes(familyKey) && isFiniteNumber(r.portfolio_return);
  });
  if (!filtered.length) return NaN;
  return filtered.reduce((sum, r) => sum + r.portfolio_return, 0) / filtered.length;
}

function computeStdDev(values) {
  const valid = values.filter(isFiniteNumber);
  if (valid.length < 2) return NaN;

  const avg = valid.reduce((sum, v) => sum + v, 0) / valid.length;
  const variance =
    valid.reduce((sum, v) => sum + (v - avg) ** 2, 0) / valid.length;

  return Math.sqrt(variance);
}

function computeMacroBalanceScore(values, dispersionScale = 0.0075) {
  const valid = values.filter(isFiniteNumber);
  if (valid.length < 2) return NaN;

  const stdev = computeStdDev(valid);
  if (!isFiniteNumber(stdev)) return NaN;

  return clamp(100 * Math.exp(-stdev / dispersionScale), 0, 100);
}

function computeMacroBalanceDownsideScore(
  values,
  dispersionScale = 0.0075,
  downsideScale = 0.01
) {
  const valid = values.filter(isFiniteNumber);
  if (valid.length < 2) return NaN;

  const stdev = computeStdDev(valid);
  if (!isFiniteNumber(stdev)) return NaN;

  const worstRegimeReturn = Math.min(...valid);
  const downsidePenalty = Math.max(0, -worstRegimeReturn);

  return clamp(
    100 *
      Math.exp(-stdev / dispersionScale) *
      Math.exp(-downsidePenalty / downsideScale),
    0,
    100
  );
}

function computeEffectiveBetsScore(neffCorr, usedCount) {
  if (!isFiniteNumber(neffCorr) || !isFiniteNumber(usedCount) || usedCount < 2) {
    return NaN;
  }

  return clamp(((neffCorr - 1) / (usedCount - 1)) * 100, 0, 100);
}

function computePortfolioScore({
  macroBalanceScore,
  macroBalanceDownsideScore,
  defensiveScore50,
  diversificationScore,
  effectiveBetsScore,
  valueScore,
}) {
  const pieces = [
    { value: macroBalanceScore, weight: 0.2 },
    { value: macroBalanceDownsideScore, weight: 0.15 },
    { value: defensiveScore50, weight: 0.2 },
    { value: diversificationScore, weight: 0.2 },
    { value: valueScore, weight: 0.15 },
    { value: effectiveBetsScore, weight: 0.1 },
  ].filter((x) => isFiniteNumber(x.value));

  if (!pieces.length) return NaN;

  const totalWeight = pieces.reduce((sum, x) => sum + x.weight, 0);
  const weightedSum = pieces.reduce((sum, x) => sum + x.value * x.weight, 0);

  return totalWeight > 0 ? weightedSum / totalWeight : NaN;
}

function MetricItem({ label, value, valueStyle = {} }) {
  return (
    <div
      style={{
        border: "1px solid #f3f4f6",
        borderRadius: 10,
        padding: 10,
        background: "#fff",
        minHeight: 72,
      }}
    >
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color: "#111827", ...valueStyle }}>
        {value}
      </div>
    </div>
  );
}

function PortfolioMacroStripChart({
  rows,
  currentPortfolioValue,
  title,
  subtitle,
  isMonthly = false,
  isMobile = false,
}) {
  const chartHeight = isMobile ? 238 : 280;
  const plotTop = 22;
  const plotHeight = isMobile ? 138 : 180;
  const leftAxisWidth = isMobile ? 42 : 64;
  const availableWidth =
    typeof window !== "undefined"
      ? Math.max(280, window.innerWidth - 48)
      : 360;
  const chartWidthMobile = Math.max(260, availableWidth - leftAxisWidth);
  const colWidth = isMobile
    ? Math.max(8, chartWidthMobile / Math.max(rows.length, 1))
    : isMonthly
    ? 46
    : 28;
  const gap = isMobile ? 0 : isMonthly ? 10 : 4;
  const minChartWidth = isMobile ? chartWidthMobile : isMonthly ? 980 : 760;

  const reconstructedPortfolioValues = buildBackwardSeriesFromEnd(
    rows,
    currentPortfolioValue,
    "portfolio_return_pct"
  );

  const startingPortfolioValue = getFirstValid(reconstructedPortfolioValues);

  const reconstructedSpyValues = buildForwardSeriesFromStart(
    rows,
    startingPortfolioValue,
    "spy_return_pct"
  );

  const allSeriesValues = [
    ...reconstructedPortfolioValues,
    ...reconstructedSpyValues,
  ].filter((v) => isFiniteNumber(v));

  const minPriceRaw = allSeriesValues.length ? Math.min(...allSeriesValues) : 0;
  const maxPriceRaw = allSeriesValues.length ? Math.max(...allSeriesValues) : 1;

  const padding = Math.max(
    (maxPriceRaw - minPriceRaw) * 0.12,
    maxPriceRaw * 0.01,
    0.5
  );
  const minPrice = minPriceRaw - padding;
  const maxPrice = maxPriceRaw + padding;
  const priceRange = maxPrice - minPrice || 1;

  const chartWidth = isMobile
    ? chartWidthMobile
    : Math.max(
        minChartWidth,
        rows.length * colWidth + Math.max(0, rows.length - 1) * gap
      );

  const xForIndex = (i) => i * (colWidth + gap) + colWidth / 2;
  const yForPrice = (price) =>
    plotTop + ((maxPrice - price) / priceRange) * plotHeight;

  const points = rows.map((r, i) => {
    const portfolioPrice = reconstructedPortfolioValues[i];
    const spyPrice = reconstructedSpyValues[i];
    return {
      ...r,
      reconPortfolioPrice: portfolioPrice,
      reconSpyPrice: spyPrice,
      x: xForIndex(i),
      portfolioY: isFiniteNumber(portfolioPrice) ? yForPrice(portfolioPrice) : null,
      spyY: isFiniteNumber(spyPrice) ? yForPrice(spyPrice) : null,
    };
  });

  const validPortfolioPoints = points.filter(
    (p) => isFiniteNumber(p.x) && isFiniteNumber(p.portfolioY)
  );

  const validSpyPoints = points.filter(
    (p) => isFiniteNumber(p.x) && isFiniteNumber(p.spyY)
  );

  const portfolioPolylinePoints = validPortfolioPoints
    .map((p) => `${p.x},${p.portfolioY}`)
    .join(" ");

  const spyPolylinePoints = validSpyPoints
    .map((p) => `${p.x},${p.spyY}`)
    .join(" ");

  const uniqueRegimes = Array.from(
    new Map(rows.map((r) => [r.macro_regime, r.macro_regime])).values()
  );

  const yAxisTicks = Array.from({ length: 5 }, (_, i) => {
    const value = maxPrice - (priceRange * i) / 4;
    return {
      value,
      y: yForPrice(value),
    };
  });

  const firstPortfolio = getFirstValid(reconstructedPortfolioValues);
  const lastPortfolio = getLastValid(reconstructedPortfolioValues);
  const firstSpy = getFirstValid(reconstructedSpyValues);
  const lastSpy = getLastValid(reconstructedSpyValues);

  const portfolioPeriodReturn =
    isFiniteNumber(firstPortfolio) && isFiniteNumber(lastPortfolio) && firstPortfolio !== 0
      ? lastPortfolio / firstPortfolio - 1
      : NaN;

  const spyPeriodReturn =
    isFiniteNumber(firstSpy) && isFiniteNumber(lastSpy) && firstSpy !== 0
      ? lastSpy / firstSpy - 1
      : NaN;

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid #f1f5f9",
        borderRadius: 12,
        padding: 12,
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>

      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          marginBottom: 8,
          color: "#111827",
        }}
      >
        Portfolio:{" "}
        <span style={getReturnStyle(portfolioPeriodReturn)}>
          {fmtSignedPct(portfolioPeriodReturn, 2)}
        </span>
        {"  "}•{"  "}
        SPY:{" "}
        <span style={getReturnStyle(spyPeriodReturn)}>
          {fmtSignedPct(spyPeriodReturn, 2)}
        </span>
      </div>

      <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 10 }}>
        {subtitle}
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 18, color: "#666" }}>No chart data available.</div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
              alignItems: "center",
            }}
          >
            {uniqueRegimes.map((regime) => {
              const c = getRegimeColors(regime);
              return (
                <div
                  key={regime}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: c.bg,
                      border: `1px solid ${c.text}33`,
                      display: "inline-block",
                    }}
                  />
                  <span>{regime}</span>
                </div>
              );
            })}

            <div
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 2,
                  background: "#111827",
                  display: "inline-block",
                }}
              />
              <span>Portfolio</span>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              <span
                style={{
                  width: 18,
                  borderTop: "2px dashed #9ca3af",
                  display: "inline-block",
                }}
              />
              <span>SPY</span>
            </div>
          </div>

          <div style={{ overflowX: "hidden", overflowY: "hidden" }}>
            <div
              style={{
                position: "relative",
                width: leftAxisWidth + chartWidth,
                height: chartHeight,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: leftAxisWidth,
                  height: chartHeight,
                }}
              >
                {yAxisTicks.map((tick, idx) => (
                  <div
                    key={`tick-${idx}`}
                    style={{
                      position: "absolute",
                      top: tick.y - 7,
                      right: 8,
                      fontSize: 10,
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {formatPrice(tick.value)}
                  </div>
                ))}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: leftAxisWidth,
                  top: 0,
                  width: chartWidth,
                  height: chartHeight,
                }}
              >
                {rows.map((r, i) => {
                  const c = getRegimeColors(r.macro_regime);
                  return (
                    <div
                      key={r.date}
                      style={{
                        position: "absolute",
                        left: i * (colWidth + gap),
                        top: 0,
                        width: colWidth,
                        height: plotTop + plotHeight,
                        background: c.bg,
                        border: "1px solid rgba(0,0,0,0.03)",
                        borderRadius: 4,
                      }}
                    />
                  );
                })}

                <svg
                  width={chartWidth}
                  height={chartHeight}
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    overflow: "visible",
                  }}
                >
                  {yAxisTicks.map((tick, idx) => (
                    <line
                      key={`grid-${idx}`}
                      x1="0"
                      x2={chartWidth}
                      y1={tick.y}
                      y2={tick.y}
                      stroke="#e5e7eb"
                      strokeDasharray="3 3"
                      strokeWidth="1"
                    />
                  ))}

                  <polyline
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.75"
                    strokeDasharray="6 5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={spyPolylinePoints}
                  />

                  <polyline
                    fill="none"
                    stroke="#111827"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={portfolioPolylinePoints}
                  />

                  {!isMobile && validPortfolioPoints.map((p) => (
                    <circle
                      key={`pt-${p.date}`}
                      cx={p.x}
                      cy={p.portfolioY}
                      r="2.5"
                      fill="#111827"
                    />
                  ))}
                </svg>

                {!isMobile && validPortfolioPoints.map((p) => (
                  <div
                    key={`label-${p.date}`}
                    style={{
                      position: "absolute",
                      left: p.x,
                      top: Math.max(2, p.portfolioY - 16),
                      transform: "translateX(-50%)",
                      fontSize: 10,
                      lineHeight: 1,
                      color: "#374151",
                      background: "rgba(255,255,255,0.95)",
                      padding: "2px 4px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                    }}
                  >
                    {formatPrice(p.reconPortfolioPrice)}
                  </div>
                ))}

                {rows.map((r, i) => (
                  <div
                    key={`bottom-${r.date}`}
                    style={{
                      display: isMobile && rows.length > 16 && i % Math.ceil(rows.length / 8) !== 0 ? "none" : "block",
                      position: "absolute",
                      left: i * (colWidth + gap),
                      top: plotTop + plotHeight + 8,
                      width: colWidth,
                      textAlign: "center",
                      fontSize: 10,
                      color: "#374151",
                      lineHeight: 1.15,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>
                      {isMonthly ? formatMonthShort(r.date) : formatShortDate(r.date)}
                    </div>
                    <div
                      style={{
                        color:
                          r.relative_return_pct > 0
                            ? "#15803d"
                            : r.relative_return_pct < 0
                            ? "#b91c1c"
                            : "#6b7280",
                        fontWeight: 700,
                      }}
                    >
                      {isFiniteNumber(r.relative_return_pct)
                        ? `${r.relative_return_pct > 0 ? "+" : ""}${r.relative_return_pct.toFixed(1)}%`
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const tableThStyle = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 13,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

const tableTdStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 13,
  whiteSpace: "nowrap",
  verticalAlign: "top",
};


// function HeaderStat({ label, value }) {
//   return (
//     <div
//       style={{
//         padding: 12,
//         borderRadius: 16,
//         background: "rgba(255,255,255,0.1)",
//         border: "1px solid rgba(255,255,255,0.16)",
//         boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
//       }}
//     >
//       <div
//         style={{
//           fontSize: 10,
//           letterSpacing: "0.08em",
//           textTransform: "uppercase",
//           color: "#cbd5e1",
//           fontWeight: 800,
//           marginBottom: 6,
//         }}
//       >
//         {label}
//       </div>
//       <div style={{ fontSize: 20, fontWeight: 950, letterSpacing: "-0.04em" }}>
//         {value}
//       </div>
//     </div>
//   );
// }


// const mobileNoOverflowStyle = {
//   width: "100%",
//   maxWidth: "100%",
//   minWidth: 0,
//   boxSizing: "border-box",
//   overflowX: "hidden",
// };

// function mobileStackStyle(isMobile, desktopColumns) {
//   return {
//     display: "grid",
//     gridTemplateColumns: isMobile ? "1fr" : desktopColumns,
//     gap: isMobile ? 12 : 16,
//     width: "100%",
//     maxWidth: "100%",
//     minWidth: 0,
//     boxSizing: "border-box",
//   };
// }

// function MobileDataCard({ title, rows }) {
//   return (
//     <div
//       style={{
//         border: "1px solid #e2e8f0",
//         borderRadius: 14,
//         background: "#fff",
//         padding: 12,
//         width: "100%",
//         maxWidth: "100%",
//         boxSizing: "border-box",
//       }}
//     >
//       <div style={{ fontSize: 14, fontWeight: 900, marginBottom: 10, color: "#0f172a" }}>
//         {title}
//       </div>
//       <div style={{ display: "grid", gap: 8 }}>
//         {rows.map((r) => (
//           <div
//             key={`${title}-${r.label}`}
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               gap: 10,
//               borderBottom: "1px solid #f1f5f9",
//               paddingBottom: 6,
//               fontSize: 12,
//             }}
//           >
//             <span style={{ color: "#64748b", minWidth: 0 }}>{r.label}</span>
//             <span style={{ fontWeight: 850, color: "#0f172a", textAlign: "right" }}>
//               {r.value}
//             </span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }


function MobileStatBlock({ label, value, sublabel }) {
  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        background: "#fff",
        padding: 12,
        boxShadow: "0 8px 18px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#64748b",
          fontWeight: 900,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 950, color: "#0f172a" }}>{value}</div>
      {sublabel ? (
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{sublabel}</div>
      ) : null}
    </div>
  );
}

function MobileSection({ title, subtitle, children }) {
  return (
    <section
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        background: "rgba(255,255,255,0.94)",
        padding: 12,
        boxShadow: "0 10px 24px rgba(15,23,42,0.07)",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 950, color: "#0f172a", marginBottom: 4 }}>
        {title}
      </div>
      {subtitle ? (
        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45, marginBottom: 12 }}>
          {subtitle}
        </div>
      ) : null}
      {children}
    </section>
  );
}

function MobileRow({ label, value, valueStyle = {} }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 10,
        padding: "8px 0",
        borderBottom: "1px solid #f1f5f9",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", minWidth: 0 }}>{label}</div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          color: "#0f172a",
          textAlign: "right",
         // minWidth: 0,
          wordBreak: "break-word",
          ...valueStyle,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function MobileBar({ label, value, maxValue, formatter = (x) => x }) {
  const pct = Number.isFinite(value) && Number.isFinite(maxValue) && maxValue > 0
    ? Math.max(2, Math.min(100, (value / maxValue) * 100))
    : 0;

  return (
    <div style={{ width: "100%", boxSizing: "border-box", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 850 }}>{label}</span>
        <span style={{ fontSize: 12, color: "#334155", fontWeight: 850 }}>{formatter(value)}</span>
      </div>
      <div
        style={{
          height: 8,
          width: "100%",
          borderRadius: 999,
          background: "#e2e8f0",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
          }}
        />
      </div>
    </div>
  );
}

function MobilePortfolioDiversification({
  input,
  setInput,
  portfolioValue,
  setPortfolioValue,
  result,
  macroExposureRows,
  isMobile,
}) {
  const holdings = result?.ok ? result.normalizedHoldings || [] : [];
  const riskRows = result?.ok ? result.riskTable || [] : [];
  const sectorRows = result?.ok ? result.sectorWeights || [] : [];
  const macroRows = result?.ok ? result.portfolioMacroSummaryRows || [] : [];
  const maxWeight = holdings.length
    ? Math.max(...holdings.map((h) => Number(h.weightPct)).filter(Number.isFinite))
    : 0;

  const maxSectorWeight = sectorRows.length
    ? Math.max(...sectorRows.map((h) => Number(h.weightPct)).filter(Number.isFinite))
    : 0;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        minHeight: "100vh",
        boxSizing: "border-box",
        overflowX: "hidden",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        padding: 10,
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 12,
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
        color: "#0f172a",
      }}
    >
      <section
        style={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 22,
          padding: 16,
          background:
            "linear-gradient(135deg, #0f172a 0%, #111827 35%, #1e293b 100%)",
          color: "white",
          boxShadow: "0 18px 40px rgba(15,23,42,0.22)",
          overflow: "hidden",
        }}
      >
        <div style={eyebrow}>Portfolio Diversification</div>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            letterSpacing: "-0.05em",
            lineHeight: 1.05,
            color: "white",
          }}
        >
          Majorah Portfolio Builder
        </h1>
        <p style={{ margin: "10px 0 0", color: "#cbd5e1", fontSize: 13, lineHeight: 1.5 }}>
        </p>
      </section>

      <MobileSection title="Inputs" subtitle="Edit tickers and weights, then scroll down for the full analysis.">
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
          One per line: <span style={{ fontFamily: "monospace" }}>TICKER weight</span>
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={8}
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            padding: 10,
            borderRadius: 12,
            border: "1px solid #cbd5e1",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 13,
            outline: "none",
            resize: "vertical",
            overflowX: "hidden",
          }}
        />

        <div style={{ fontWeight: 900, marginTop: 12, marginBottom: 6, fontSize: 13 }}>
          Portfolio value
        </div>
        <input
          value={portfolioValue}
          onChange={(e) => setPortfolioValue(e.target.value)}
          placeholder="100000"
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            padding: 10,
            borderRadius: 12,
            border: "1px solid #cbd5e1",
            fontSize: 14,
            outline: "none",
          }}
        />
      </MobileSection>

      {!result?.ok ? (
        <MobileSection title="Not enough data yet">
          <div style={{ color: "#b91c1c", fontSize: 13, lineHeight: 1.5 }}>
            {result?.reason || "Enter at least two valid tickers and weights."}
          </div>
        </MobileSection>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 10,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <MobileStatBlock
              label="Portfolio Score"
              value={isFiniteNumber(result.portfolioScore) ? result.portfolioScore.toFixed(0) : "—"}
            />
            <MobileStatBlock
              label="Diversity Score"
              value={isFiniteNumber(result.diversificationScore) ? result.diversificationScore.toFixed(0) : "—"}
              sublabel="Equal 4-quadrant balance = higher"
            />
            <MobileStatBlock
              label="Defensive Score"
              value={isFiniteNumber(result.defensiveScore50) ? result.defensiveScore50.toFixed(0) : "—"}
              sublabel={`Beta to SPY: ${fmtNum(result.betaSpy, 2)}`}
            />
            <MobileStatBlock
              label="Volatility Score"
              value={isFiniteNumber(result.volatilityScore50) ? result.volatilityScore50.toFixed(0) : "—"}
              sublabel={`Annual vol: ${isFiniteNumber(result.volP) ? fmtPct(result.volP, 2) : "—"}`}
            />
            <MobileStatBlock
              label="Value Score"
              value={isFiniteNumber(result.valueScore) ? result.valueScore.toFixed(0) : "—"}
              sublabel="Low P/B, PEG, Fwd P/E = higher"
            />
          </div>

          <MobileSection title="Key Metrics">
            <MobileRow label="Expected Annual Return" value={fmtPct(result.portReturn, 2)} />
            <MobileRow label="Sharpe Ratio" value={fmtNum(result.sharpe, 2)} />
            <MobileRow label="Value Score" value={isFiniteNumber(result.valueScore) ? result.valueScore.toFixed(0) : "—"} />
            <MobileRow label="Beta to SPY" value={fmtNum(result.betaSpy, 2)} />
            <MobileRow label="Correlation to SPY" value={fmtNum(result.corrSpy, 2)} />
            <MobileRow label="Portfolio RSI 14" value={fmtNum(result.portfolioRSI14, 1)} />
            <MobileRow label="Dividend Yield" value={fmtPct(result.portfolioDividendYield, 2)} />
            <MobileRow label="Annual Dividend Income" value={fmtCurrency(result.annualDividendIncome, 0)} />
            <MobileRow label="Up When SPY Down" value={fmtPct(result.upWhenSpyDownRate, 1)} />
            <MobileRow label="Up When SPY Down >1.5%" value={fmtPct(result.upWhenSpyDown15Rate, 1)} />
          </MobileSection>

          <MobileSection title="Holdings">
            {holdings.map((h) => (
              <MobileBar
                key={h.ticker}
                label={h.ticker}
                value={h.weightPct}
                maxValue={maxWeight}
                formatter={(v) => (Number.isFinite(v) ? `${v.toFixed(2)}%` : "—")}
              />
            ))}
          </MobileSection>

          <MobileSection title="Risk Contribution">
            {riskRows.map((r) => (
              <div
                key={r.ticker}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: 10,
                  marginBottom: 10,
                  background: "#fff",
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 950, marginBottom: 6 }}>{r.ticker}</div>
                <MobileRow label="Weight" value={`${r.weightPct.toFixed(2)}%`} />
                <MobileRow label="Annual Volatility" value={`${r.volAnnualPct.toFixed(2)}%`} />
                <MobileRow label="Expected Return" value={`${r.expAnnualPct.toFixed(2)}%`} />
                <MobileRow label="Risk Contribution" value={fmtPct(r.pctVariance, 1)} />
                <MobileRow label="Sector" value={r.sector || "Unknown"} />
              </div>
            ))}
          </MobileSection>

          <MobileSection title="Sector Weights">
            {sectorRows.map((s) => (
              <MobileBar
                key={s.sector}
                label={s.sector}
                value={s.weightPct}
                maxValue={maxSectorWeight}
                formatter={(v) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : "—")}
              />
            ))}
          </MobileSection>

          <MobileSection title="Holdings Macro Map" subtitle="Inflation × growth exposure for only the tickers in this portfolio.">
            <PortfolioMacroMap
              holdings={holdings}
              macroExposureRows={macroExposureRows}
              isMobile={isMobile}
            />
          </MobileSection>

          <MobileSection title="Macro Regime Summary">
            {macroRows.length ? (
              macroRows.map((r) => (
                <div
                  key={r.macro_regime}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    padding: 10,
                    marginBottom: 10,
                    background: "#fff",
                  }}
                >
                  <div style={getRegimeStyle(r.macro_regime)}>{r.macro_regime}</div>
                  <MobileRow label="Days" value={r.total_days} />
                  <MobileRow label="Win Rate" value={fmtPct(r.win_rate, 1)} />
                  <MobileRow
                    label="Avg Daily Return"
                    value={fmtSignedPct(r.avg_daily_return, 2)}
                    valueStyle={getReturnStyle(r.avg_daily_return)}
                  />
                  <MobileRow
                    label="Avg Return vs SPY"
                    value={fmtSignedPct(r.avg_return_vs_spy, 2)}
                    valueStyle={getReturnStyle(r.avg_return_vs_spy)}
                  />
                </div>
              ))
            ) : (
              <div style={{ color: "#64748b", fontSize: 13 }}>No macro regime data available.</div>
            )}
          </MobileSection>

          <MobileSection title="Best Macro Fit">
            <MobileRow label="Best Portfolio Macro Type" value={result.bestPortfolioMacroType || "—"} />
            <MobileRow label="Macro Balance Score" value={isFiniteNumber(result.macroBalanceScore) ? result.macroBalanceScore.toFixed(0) : "—"} />
            <MobileRow label="Macro Downside Score" value={isFiniteNumber(result.macroBalanceDownsideScore) ? result.macroBalanceDownsideScore.toFixed(0) : "—"} />
            <MobileRow label="Avg Stagflation Return" value={fmtSignedPct(result.avgReturnStagflation, 2)} valueStyle={getReturnStyle(result.avgReturnStagflation)} />
            <MobileRow label="Avg Deflationary Recession Return" value={fmtSignedPct(result.avgReturnDeflationaryRecession, 2)} valueStyle={getReturnStyle(result.avgReturnDeflationaryRecession)} />
            <MobileRow label="Avg Deflationary Growth Return" value={fmtSignedPct(result.avgReturnDeflationaryGrowth, 2)} valueStyle={getReturnStyle(result.avgReturnDeflationaryGrowth)} />
            <MobileRow label="Avg Inflationary Growth Return" value={fmtSignedPct(result.avgReturnInflationaryGrowth, 2)} valueStyle={getReturnStyle(result.avgReturnInflationaryGrowth)} />
          </MobileSection>
        </>
      )}
    </div>
  );
}



function PortfolioMacroMap({ holdings, macroExposureRows, isMobile }) {
  const macroByTickerRaw = useMemo(() => {
    const map = new Map();

    for (const row of macroExposureRows || []) {
      const ticker = getTickerFromAnyRow(row);
      if (!ticker) continue;

      map.set(ticker, {
        raw: row,
        ticker,
        inflationRaw: getMacroExposure(row, "inflation"),
        growthRaw: getMacroExposure(row, "growth"),
        liquidityRaw: getMacroExposure(row, "liquidity"),
      });
    }

    return map;
  }, [macroExposureRows]);

  const macroScaleValues = useMemo(() => {
    const vals = [];

    for (const row of macroByTickerRaw.values()) {
      if (Number.isFinite(row.inflationRaw)) vals.push(row.inflationRaw);
      if (Number.isFinite(row.growthRaw)) vals.push(row.growthRaw);
      if (Number.isFinite(row.liquidityRaw)) vals.push(row.liquidityRaw);
    }

    return vals;
  }, [macroByTickerRaw]);

  const data = useMemo(() => {
    return (holdings || [])
      .map((h) => {
        const ticker = normalizeTicker(h?.ticker);
        const macro = macroByTickerRaw.get(ticker);
        if (!macro) return null;

        const inflation = normalizeExposureForChart(macro.inflationRaw, macroScaleValues);
        const growth = normalizeExposureForChart(macro.growthRaw, macroScaleValues);
        const liquidity = normalizeExposureForChart(macro.liquidityRaw, macroScaleValues);

        if (!Number.isFinite(inflation) || !Number.isFinite(growth)) return null;

        const weightPct = Number(h?.weightPct ?? h?.weight);
        const size = Number.isFinite(weightPct)
          ? Math.max(5, Math.min(20, 5 + weightPct / 2.5))
          : 7;

        return {
          ticker,
          inflation,
          growth,
          liquidity,
          weightPct,
          size,
          z: size,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (Number(b.weightPct) || 0) - (Number(a.weightPct) || 0));
  }, [holdings, macroByTickerRaw, macroScaleValues]);

  const chartHeight = isMobile ? 420 : 560;

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid #f1f5f9",
        borderRadius: 12,
        padding: isMobile ? 10 : 14,
        background: "#ffffff",
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4, color: "#111827" }}>
        Holdings Macro Map
      </div>
      <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 10, color: "#374151" }}>
        X-axis = inflation exposure. Y-axis = growth exposure. Bubble size = portfolio weight.
        Top-right = inflationary growth, top-left = deflationary growth, bottom-right = stagflation-type,
        bottom-left = deflationary recession-type.
      </div>

      {data.length === 0 ? (
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            color: "#9a3412",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          No portfolio holdings matched stock_macro_exposures.json.
        </div>
      ) : (
        <div style={{ width: "100%", height: chartHeight, minHeight: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{
                top: 26,
                right: isMobile ? 10 : 28,
                bottom: 38,
                left: isMobile ? 6 : 28,
              }}
            >
              <defs>
                <linearGradient id="portfolioMacroBg" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#eff6ff" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#ffffff" stopOpacity={0.65} />
                  <stop offset="100%" stopColor="#fef3c7" stopOpacity={0.85} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <ReferenceArea x1={-1} x2={1} y1={-1} y2={1} fill="url(#portfolioMacroBg)" fillOpacity={0.95} />
              <ReferenceArea x1={0} x2={1} y1={0} y2={1} fill="#f5f3ff" fillOpacity={0.35} />
              <ReferenceArea x1={-1} x2={0} y1={0} y2={1} fill="#fffbeb" fillOpacity={0.45} />
              <ReferenceArea x1={0} x2={1} y1={-1} y2={0} fill="#ecfdf5" fillOpacity={0.45} />
              <ReferenceArea x1={-1} x2={0} y1={-1} y2={0} fill="#eff6ff" fillOpacity={0.45} />

              <ReferenceLine x={0} stroke="#334155" strokeWidth={1.5} />
              <ReferenceLine y={0} stroke="#334155" strokeWidth={1.5} />

              {!isMobile && (
                <>
                  <text x="75%" y="8%" textAnchor="middle" fill="#6d28d9" fontSize="12" fontWeight="900">
                    Inflationary Growth
                  </text>
                  <text x="25%" y="8%" textAnchor="middle" fill="#047857" fontSize="12" fontWeight="900">
                    Deflationary Growth
                  </text>
                  <text x="75%" y="95%" textAnchor="middle" fill="#b45309" fontSize="12" fontWeight="900">
                    Stagflation-Type
                  </text>
                  <text x="25%" y="95%" textAnchor="middle" fill="#1d4ed8" fontSize="12" fontWeight="900">
                    Deflationary Recession
                  </text>
                </>
              )}

              <XAxis
                type="number"
                dataKey="inflation"
                domain={[-1.05, 1.05]}
                tickFormatter={(v) => `${Math.round(v * 100)}`}
                tick={{ fontSize: isMobile ? 10 : 12, fill: "#475569" }}
                label={{
                  value: "Inflation Exposure",
                  position: "insideBottom",
                  offset: -14,
                  fill: "#334155",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 800,
                }}
              />
              <YAxis
                type="number"
                dataKey="growth"
                domain={[-1.05, 1.05]}
                tickFormatter={(v) => `${Math.round(v * 100)}`}
                tick={{ fontSize: isMobile ? 10 : 12, fill: "#475569" }}
                label={{
                  value: "Growth Exposure",
                  angle: -90,
                  position: "insideLeft",
                  fill: "#334155",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 800,
                }}
              />
              <ZAxis type="number" dataKey="z" range={[70, isMobile ? 240 : 520]} />

              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0]?.payload;
                  return (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        padding: 10,
                        boxShadow: "0 8px 22px rgba(15,23,42,0.12)",
                        fontSize: 12,
                        color: "#111827",
                      }}
                    >
                      <div style={{ fontWeight: 900, fontSize: 15, marginBottom: 4 }}>{p?.ticker}</div>
                      <div>Weight: {Number.isFinite(p?.weightPct) ? `${p.weightPct.toFixed(2)}%` : "—"}</div>
                      <div>Inflation exposure: {Number.isFinite(p?.inflation) ? Math.round(p.inflation * 100) : "—"}</div>
                      <div>Growth exposure: {Number.isFinite(p?.growth) ? Math.round(p.growth * 100) : "—"}</div>
                      <div>Liquidity exposure: {Number.isFinite(p?.liquidity) ? Math.round(p.liquidity * 100) : "—"}</div>
                    </div>
                  );
                }}
              />

              <Scatter
                data={data}
                fill="#2563eb"
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  if (cx == null || cy == null) return null;
                  const r = isMobile ? 5 : Math.max(5, Math.min(14, payload?.size || 7));

                  return (
                    <g>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="#2563eb"
                        stroke="#ffffff"
                        strokeWidth={1.5}
                        opacity={0.9}
                      />
                      <text
                        x={cx + r + 5}
                        y={cy + 4}
                        fontSize={isMobile ? 8 : 11}
                        fill="#0f172a"
                        fontWeight={900}
                        pointerEvents="none"
                      >
                        {payload?.ticker}
                      </text>
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function PortfolioDiversification() {
  const isMobile = useIsMobile(900);
  const [corrCsv, setCorrCsv] = useState("");
  const [volsJson, setVolsJson] = useState(null);
  const [expJson, setExpJson] = useState(null);
  const [dailyReturnsCsv, setDailyReturnsCsv] = useState("");
  const [divJson, setDivJson] = useState(null);
  const [sectorJson, setSectorJson] = useState(null);
  const [macroJson, setMacroJson] = useState([]);
  const [macroExposureJson, setMacroExposureJson] = useState([]);
  const [stockScreenerJson, setStockScreenerJson] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [input, setInput] = useState("GOOGL 33.33\nMSFT 33.33\nAMZN 33.34");
  const [portfolioValue, setPortfolioValue] = useState("100000");

  useEffect(() => {
    let alive = true;

    async function loadAll() {
      try {
        setLoading(true);
        setErr("");

        const [
          corrRes,
          volsRes,
          expRes,
          dailyRes,
          divRes,
          sectorRes,
          macroPayload,
          macroExposureRes,
          stockScreenerRes,
        ] = await Promise.all([
          fetch(CORR_CSV_URL, { cache: "no-store" }),
          fetch(VOLS_JSON_URL, { cache: "no-store" }),
          fetch(EXPECTED_RETURNS_URL, { cache: "no-store" }),
          fetch(DAILY_RETURNS_URL, { cache: "no-store" }),
          fetch(DIVIDENDS_URL, { cache: "no-store" }),
          fetch(SECTORS_URL, { cache: "no-store" }),
          fetchJsonFromCandidates(MACRO_CANDIDATE_URLS),
          fetch(STOCK_MACRO_EXPOSURES_URL, { cache: "no-store" }),
          fetch(STOCK_SCREENER_URL, { cache: "no-store" }),
        ]);

        const [
          corrText,
          volsText,
          expText,
          dailyText,
          divText,
          sectorText,
          macroExposureText,
          stockScreenerText,
        ] = await Promise.all([
          corrRes.text(),
          volsRes.text(),
          expRes.text(),
          dailyRes.text(),
          divRes.text(),
          sectorRes.text(),
          macroExposureRes.text(),
          stockScreenerRes.text(),
        ]);

        if (!corrRes.ok) {
          throw new Error(
            `HTTP ${corrRes.status} fetching correlation_matrix.csv. First 120 chars: ${corrText.slice(
              0,
              120
            )}`
          );
        }
        if (corrText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of CSV for correlation_matrix.csv. First 120 chars: ${corrText.slice(
              0,
              120
            )}`
          );
        }

        if (!volsRes.ok) {
          throw new Error(
            `HTTP ${volsRes.status} fetching vols.json. First 120 chars: ${volsText.slice(
              0,
              120
            )}`
          );
        }
        if (volsText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for vols.json. First 120 chars: ${volsText.slice(
              0,
              120
            )}`
          );
        }

        if (!expRes.ok) {
          throw new Error(
            `HTTP ${expRes.status} fetching expected_returns.json. First 120 chars: ${expText.slice(
              0,
              120
            )}`
          );
        }
        if (expText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for expected_returns.json. First 120 chars: ${expText.slice(
              0,
              120
            )}`
          );
        }

        if (!dailyRes.ok) {
          throw new Error(
            `HTTP ${dailyRes.status} fetching daily_returns.csv. First 120 chars: ${dailyText.slice(
              0,
              120
            )}`
          );
        }
        if (dailyText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of CSV for daily_returns.csv. First 120 chars: ${dailyText.slice(
              0,
              120
            )}`
          );
        }

        if (!divRes.ok) {
          throw new Error(
            `HTTP ${divRes.status} fetching dividends.json. First 120 chars: ${divText.slice(
              0,
              120
            )}`
          );
        }
        if (divText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for dividends.json. First 120 chars: ${divText.slice(
              0,
              120
            )}`
          );
        }

        if (!sectorRes.ok) {
          throw new Error(
            `HTTP ${sectorRes.status} fetching sectors.json. First 120 chars: ${sectorText.slice(
              0,
              120
            )}`
          );
        }
        if (sectorText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for sectors.json. First 120 chars: ${sectorText.slice(
              0,
              120
            )}`
          );
        }

        if (!macroExposureRes.ok) {
          throw new Error(
            `HTTP ${macroExposureRes.status} fetching stock_macro_exposures.json. First 120 chars: ${macroExposureText.slice(
              0,
              120
            )}`
          );
        }
        if (macroExposureText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for stock_macro_exposures.json. First 120 chars: ${macroExposureText.slice(
              0,
              120
            )}`
          );
        }

        if (!stockScreenerRes.ok) {
          throw new Error(
            `HTTP ${stockScreenerRes.status} fetching stock_screener.json. First 120 chars: ${stockScreenerText.slice(
              0,
              120
            )}`
          );
        }
        if (stockScreenerText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for stock_screener.json. First 120 chars: ${stockScreenerText.slice(
              0,
              120
            )}`
          );
        }

        const vols = JSON.parse(volsText);
        const exp = JSON.parse(expText);
        const div = JSON.parse(divText);
        const sectors = JSON.parse(sectorText);
        const macro = Array.isArray(macroPayload?.json) ? macroPayload.json : [];
        const macroExposures = normalizeMacroExposurePayload(JSON.parse(macroExposureText));
        const stockScreenerRows = normalizeStockScreenerPayload(JSON.parse(stockScreenerText));

        if (alive) {
          setCorrCsv(corrText);
          setVolsJson(vols);
          setExpJson(exp);
          setDailyReturnsCsv(dailyText);
          setDivJson(div);
          setSectorJson(sectors);
          setMacroJson(macro);
          setMacroExposureJson(macroExposures);
          setStockScreenerJson(stockScreenerRows);
        }
      } catch (e) {
        if (alive) setErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAll();
    return () => {
      alive = false;
    };
  }, []);

  const holdings = useMemo(() => parseHoldings(input), [input]);

  const corrParsed = useMemo(() => {
    if (!corrCsv) return { tickers: [], indexOf: new Map(), mat: [] };
    return parseCorrelationCSV(corrCsv);
  }, [corrCsv]);

  const dailyParsed = useMemo(() => {
    if (!dailyReturnsCsv) return { tickers: [], rows: [] };
    return parseDailyReturnsCSV(dailyReturnsCsv);
  }, [dailyReturnsCsv]);

  const volAnnualMap = useMemo(() => {
    const m = new Map();
    const obj =
      volsJson?.vol_annual && typeof volsJson.vol_annual === "object"
        ? volsJson.vol_annual
        : {};
    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [volsJson]);

  const expAnnualMap = useMemo(() => {
    const m = new Map();
    const obj =
      expJson?.expected_return_annual_pct &&
      typeof expJson.expected_return_annual_pct === "object"
        ? expJson.expected_return_annual_pct
        : {};
    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [expJson]);

  const dividendYieldMap = useMemo(() => {
    const m = new Map();
    const obj =
      divJson?.dividend_yield_pct &&
      typeof divJson.dividend_yield_pct === "object"
        ? divJson.dividend_yield_pct
        : {};

    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [divJson]);

  const sectorMap = useMemo(() => {
    const m = new Map();
    const obj =
      sectorJson?.sector && typeof sectorJson.sector === "object"
        ? sectorJson.sector
        : {};

    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const sector = String(v || "").trim() || "Unknown";
      if (t) m.set(t, sector);
    }
    return m;
  }, [sectorJson]);

  const portfolioValueNum = useMemo(() => {
    const cleaned = String(portfolioValue || "").replace(/[$,]/g, "").trim();
    const num = Number(cleaned);
    return Number.isFinite(num) && num >= 0 ? num : NaN;
  }, [portfolioValue]);


  const stockScreenerValueMap = useMemo(() => {
    const map = new Map();

    for (const row of stockScreenerJson || []) {
      const ticker = normalizeTicker(row?.ticker ?? row?.Ticker ?? row?.symbol ?? row?.Symbol);
      if (!ticker) continue;

      map.set(ticker, {
        forwardPE: getFirstNumeric(row, [
          "forwardPE",
          "forward_pe",
          "ForwardPE",
          "forwardP/E",
          "Forward P/E",
        ]),
        peg: getFirstNumeric(row, [
          "peg",
          "PEG",
          "pegRatio",
          "peg_ratio",
          "trailingPegRatio",
          "Trailing PEG Ratio",
        ]),
        pb: getFirstNumeric(row, [
          "pb",
          "priceToBook",
          "price_to_book",
          "P/B",
          "Price/Book",
        ]),
      });
    }

    return map;
  }, [stockScreenerJson]);

  const result = useMemo(() => {
    const { indexOf, mat } = corrParsed;

    if (!holdings.length) {
      return { ok: false, reason: "Enter at least 2 tickers + weights." };
    }
    if (!mat.length) {
      return { ok: false, reason: "Correlation matrix not loaded yet." };
    }
    if (!volAnnualMap.size) {
      return { ok: false, reason: "Volatility map not loaded yet." };
    }
    if (!expAnnualMap.size) {
      return { ok: false, reason: "Expected return map not loaded yet." };
    }
    if (!dailyParsed.rows.length) {
      return { ok: false, reason: "Daily returns matrix not loaded yet." };
    }
    if (!dailyParsed.tickers.includes("SPY")) {
      return {
        ok: false,
        reason:
          "SPY is missing from daily_returns.csv. Add SPY to the backend universe.",
      };
    }

    const present = [];
    const missing = [];
    const missingReasons = [];

    for (const h of holdings) {
      const t = normalizeTicker(h.ticker);
      const i = indexOf.get(t);
      const volA = volAnnualMap.get(t);
      const muA = expAnnualMap.get(t);
      const inDaily = dailyParsed.tickers.includes(t);

      const miss = [];
      if (i == null) miss.push("corr");
      if (volA == null) miss.push("vol");
      if (muA == null) miss.push("exp");
      if (!inDaily) miss.push("daily");

      if (miss.length) {
        missing.push(t);
        missingReasons.push({ ticker: t, missing: miss });
      } else {
        present.push({ ticker: t, weight: h.weight, idx: i, volA, muA });
      }
    }

    if (present.length < 2) {
      return {
        ok: false,
        reason:
          "Need at least 2 tickers that exist in correlation matrix, vols.json, expected_returns.json, and daily_returns.csv.",
        missing,
        missingReasons,
      };
    }

    const sumW = present.reduce((a, b) => a + b.weight, 0);
    if (!Number.isFinite(sumW) || sumW <= 0) {
      return {
        ok: false,
        reason: "Weights must sum to a positive number.",
        missing,
      };
    }

    const n = present.length;
    const w = present.map((p) => p.weight / sumW);

    let num = 0;
    let den = 0;
    let nanPairs = 0;
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const rho = mat[present[a].idx][present[b].idx];
        const wij = w[a] * w[b];
        den += wij;
        if (Number.isFinite(rho)) num += wij * rho;
        else nanPairs += 1;
      }
    }
    const rhoBar = den > 0 ? num / den : NaN;

    let qCorr = 0;
    for (let a = 0; a < n; a++) {
      for (let b = 0; b < n; b++) {
        const rho = mat[present[a].idx][present[b].idx];
        const safe = Number.isFinite(rho) ? rho : a === b ? 1 : 0;
        qCorr += w[a] * w[b] * safe;
      }
    }

    const neffCorr = qCorr > 0 ? 1 / qCorr : NaN;

    const effectiveBetsDiversificationScore =
      Number.isFinite(neffCorr) && n > 1
        ? clamp(((neffCorr - 1) / (n - 1)) * 100, 0, 100)
        : n === 1
        ? 0
        : NaN;

    const volDec = present.map((p) => p.volA / 100.0);

    let varP = 0;
    for (let a = 0; a < n; a++) {
      for (let b = 0; b < n; b++) {
        const rhoRaw = mat[present[a].idx][present[b].idx];
        const rho = Number.isFinite(rhoRaw) ? rhoRaw : a === b ? 1 : 0;
        varP += w[a] * w[b] * rho * volDec[a] * volDec[b];
      }
    }
    const volP = varP > 0 ? Math.sqrt(varP) : NaN;
    const dailyMove = Number.isFinite(volP)
      ? volP / Math.sqrt(TRADING_DAYS)
      : NaN;

    const wAvgVol = volDec.reduce((acc, s, i) => acc + w[i] * s, 0);
    const divRatio = Number.isFinite(volP) && volP > 0 ? wAvgVol / volP : NaN;

    const muDec = present.map((p) => p.muA / 100.0);
    const portReturn = muDec.reduce((acc, mu, i) => acc + w[i] * mu, 0);

    const sharpe =
      Number.isFinite(volP) && volP > 0
        ? (portReturn - RISK_FREE_RATE) / volP
        : NaN;

    const m = new Array(n).fill(0);
    for (let a = 0; a < n; a++) {
      let sum = 0;
      for (let b = 0; b < n; b++) {
        const rhoRaw = mat[present[a].idx][present[b].idx];
        const rho = Number.isFinite(rhoRaw) ? rhoRaw : a === b ? 1 : 0;
        const cov = rho * volDec[a] * volDec[b];
        sum += w[b] * cov;
      }
      m[a] = sum;
    }

    const varContrib = m.map((mi, i) => w[i] * mi);
    const pctVarContrib =
      varP > 0 ? varContrib.map((x) => x / varP) : varContrib.map(() => NaN);

    const riskTable = present
      .map((p, i) => ({
        ticker: p.ticker,
        weightPct: w[i] * 100,
        expAnnualPct: p.muA,
        volAnnualPct: p.volA,
        pctVariance: pctVarContrib[i],
        dividendYieldPct: dividendYieldMap.get(p.ticker) ?? NaN,
        sector: sectorMap.get(p.ticker) || "Unknown",
      }))
      .sort((a, b) => (b.pctVariance ?? -1) - (a.pctVariance ?? -1));

    const normalizedHoldings = present
      .map((p, i) => ({ ticker: p.ticker, weightPct: w[i] * 100 }))
      .sort((a, b) => b.weightPct - a.weightPct);

    const sectorWeightsMap = new Map();
    for (let i = 0; i < present.length; i++) {
      const ticker = present[i].ticker;
      const sector = sectorMap.get(ticker) || "Unknown";
      const prev = sectorWeightsMap.get(sector) || 0;
      sectorWeightsMap.set(sector, prev + w[i]);
    }

    const sectorWeights = Array.from(sectorWeightsMap.entries())
      .map(([sector, weight]) => ({
        sector,
        weight,
        weightPct: weight * 100,
      }))
      .sort((a, b) => b.weight - a.weight);

    const portfolioSeries = [];

    for (const row of dailyParsed.rows) {
      let rp = 0;
      let valid = true;

      for (let i = 0; i < present.length; i++) {
        const t = present[i].ticker;
        const riPct = row.values.get(t);

        if (!Number.isFinite(riPct)) {
          valid = false;
          break;
        }

        rp += w[i] * (riPct / 100.0);
      }

      const spyPct = row.values.get("SPY");

      if (!valid || !Number.isFinite(spyPct)) continue;

      portfolioSeries.push({
        date: normalizeDateString(row.date),
        rp,
        spy: spyPct / 100.0,
      });
    }

    if (portfolioSeries.length < 20) {
      return {
        ok: false,
        reason:
          "Not enough overlapping daily return history across your holdings and SPY to compute defensive metrics / RSI.",
        missing,
        missingReasons,
      };
    }

    const rpArr = portfolioSeries.map((x) => x.rp);
    const spyArr = portfolioSeries.map((x) => x.spy);

    const meanRp = mean(rpArr);
    const meanSpy = mean(spyArr);

    let covPS = 0;
    let varSpy = 0;
    let varRp = 0;

    for (let i = 0; i < portfolioSeries.length; i++) {
      const a = rpArr[i] - meanRp;
      const b = spyArr[i] - meanSpy;
      covPS += a * b;
      varRp += a * a;
      varSpy += b * b;
    }

    if (portfolioSeries.length > 1) {
      covPS /= portfolioSeries.length - 1;
      varRp /= portfolioSeries.length - 1;
      varSpy /= portfolioSeries.length - 1;
    } else {
      covPS = NaN;
      varRp = NaN;
      varSpy = NaN;
    }

    const betaSpy =
      Number.isFinite(covPS) && Number.isFinite(varSpy) && varSpy > 0
        ? covPS / varSpy
        : NaN;

    const corrSpy =
      Number.isFinite(covPS) &&
      Number.isFinite(varRp) &&
      Number.isFinite(varSpy) &&
      varRp > 0 &&
      varSpy > 0
        ? covPS / Math.sqrt(varRp * varSpy)
        : NaN;

    const downDays = portfolioSeries.filter((x) => x.spy < 0);

    const avgPortDown = downDays.length
      ? downDays.reduce((a, x) => a + x.rp, 0) / downDays.length
      : NaN;

    const avgSpyDown = downDays.length
      ? downDays.reduce((a, x) => a + x.spy, 0) / downDays.length
      : NaN;

    const downsideCapture =
      Number.isFinite(avgPortDown) &&
      Number.isFinite(avgSpyDown) &&
      avgSpyDown !== 0
        ? avgPortDown / avgSpyDown
        : NaN;

    const upWhenSpyDownRate = downDays.length
      ? downDays.filter((x) => x.rp > 0).length / downDays.length
      : NaN;

    const down15Days = portfolioSeries.filter((x) => x.spy <= -0.015);

    const upWhenSpyDown15Rate = down15Days.length
      ? down15Days.filter((x) => x.rp > 0).length / down15Days.length
      : NaN;

    const portDailyVol = Number.isFinite(varRp) ? Math.sqrt(varRp) : NaN;
    const spyDailyVol = Number.isFinite(varSpy) ? Math.sqrt(varSpy) : NaN;

    const relativeVolToSpy =
      Number.isFinite(portDailyVol) &&
      Number.isFinite(spyDailyVol) &&
      spyDailyVol > 0
        ? portDailyVol / spyDailyVol
        : NaN;

    const portfolioRSI14 = computeRSIFromReturns(rpArr, 14);

    const volatilityScore50 = scoreVolatilityHighIsHigh(relativeVolToSpy);
    const defensivenessScore50 = scoreBetaDefensive(betaSpy);

    const portfolioDividendYield =
      present.reduce((acc, p, i) => {
        const y = dividendYieldMap.get(p.ticker);
        if (!Number.isFinite(y)) return acc;
        return acc + w[i] * (y / 100.0);
      }, 0) || 0;

    const annualDividendIncome = Number.isFinite(portfolioValueNum)
      ? portfolioValueNum * portfolioDividendYield
      : NaN;

    const treemapData = normalizedHoldings.map((h) => ({
      name: h.ticker,
      size: h.weightPct,
      weightPct: h.weightPct,
    }));

    const portfolioReturnByDate = new Map(
      portfolioSeries.map((x) => [normalizeDateString(x.date), x])
    );

    const portfolioMacroRows = Array.isArray(macroJson)
      ? macroJson
          .map((m) => {
            const date = normalizeDateString(m?.date);
            const portRow = portfolioReturnByDate.get(date);
            if (!portRow) return null;

            return {
              date,
              portfolio_return: portRow.rp,
              spy_return: portRow.spy,
              relative_return:
                isFiniteNumber(portRow.rp) && isFiniteNumber(portRow.spy)
                  ? portRow.rp - portRow.spy
                  : null,
              inflation_score: Number.isFinite(Number(m?.inflation_score))
                ? Number(m.inflation_score)
                : null,
              growth_score: Number.isFinite(Number(m?.growth_score))
                ? Number(m.growth_score)
                : null,
              liquidity_score: Number.isFinite(Number(m?.liquidity_score))
                ? Number(m.liquidity_score)
                : null,
            };
          })
          .filter(Boolean)
          .map((r) => ({
            ...r,
            macro_regime: getMacroRegime(r),
          }))
      : [];

    const portfolioMacroSummaryRows = (() => {
      const groups = new Map();

      for (const row of portfolioMacroRows) {
        const key = row.macro_regime || "—";
        if (!groups.has(key)) {
          groups.set(key, {
            macro_regime: key,
            count: 0,
            sum_return: 0,
            sum_relative: 0,
            num_up: 0,
            count_return: 0,
            count_relative: 0,
          });
        }

        const g = groups.get(key);
        g.count += 1;

        if (isFiniteNumber(row.portfolio_return)) {
          g.sum_return += row.portfolio_return;
          g.count_return += 1;
          if (row.portfolio_return > 0) g.num_up += 1;
        }

        if (isFiniteNumber(row.relative_return)) {
          g.sum_relative += row.relative_return;
          g.count_relative += 1;
        }
      }

      return [...groups.values()]
        .map((g) => ({
          macro_regime: g.macro_regime,
          total_days: g.count,
          win_rate: g.count_return > 0 ? g.num_up / g.count_return : null,
          avg_daily_return:
            g.count_return > 0 ? g.sum_return / g.count_return : null,
          avg_return_vs_spy:
            g.count_relative > 0 ? g.sum_relative / g.count_relative : null,
        }))
        .sort((a, b) => {
          const aVal = isFiniteNumber(a.avg_daily_return)
            ? a.avg_daily_return
            : -999;
          const bVal = isFiniteNumber(b.avg_daily_return)
            ? b.avg_daily_return
            : -999;
          return bVal - aVal;
        });
    })();

    const portfolioRadarData = (() => {
      const defs = [
        { key: "inflation_score", metric: "Inflation" },
        { key: "growth_score", metric: "Growth" },
        { key: "liquidity_score", metric: "Liquidity" },
      ];

      const raw = defs.map(({ key, metric }) => {
        const matched = portfolioMacroRows.filter(
          (r) =>
            isFiniteNumber(r?.[key]) &&
            r[key] > 0 &&
            isFiniteNumber(r.portfolio_return)
        );

        const avgReturn =
          matched.length > 0
            ? matched.reduce((sum, r) => sum + r.portfolio_return, 0) /
              matched.length
            : null;

        return {
          metric,
          avg_return: avgReturn,
          count: matched.length,
        };
      });

      const vals = raw
        .map((r) => r.avg_return)
        .filter((v) => isFiniteNumber(v));

      const min = vals.length ? Math.min(...vals) : 0;
      const max = vals.length ? Math.max(...vals) : 0;

      return raw.map((r) => {
        let score = 50;

        if (isFiniteNumber(r.avg_return) && max !== min) {
          score = 20 + ((r.avg_return - min) / (max - min)) * 80;
        } else if (isFiniteNumber(r.avg_return) && max === min) {
          score = 60;
        }

        return {
          ...r,
          score: Math.round(score),
          fullMark: 100,
        };
      });
    })();

    const bestPortfolioMacroType = (() => {
      if (!portfolioMacroSummaryRows.length) return "—";
      const valid = portfolioMacroSummaryRows.filter((r) =>
        isFiniteNumber(r.avg_daily_return)
      );
      if (!valid.length) return "—";
      return valid[0]?.macro_regime || "—";
    })();

    const sortedPortfolioMacroRows = [...portfolioMacroRows].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    const last30PortfolioRows = sortedPortfolioMacroRows.slice(-30).map((r) => ({
      date: r.date,
      macro_regime: r.macro_regime,
      portfolio_return_pct: isFiniteNumber(r.portfolio_return)
        ? r.portfolio_return * 100
        : null,
      spy_return_pct: isFiniteNumber(r.spy_return) ? r.spy_return * 100 : null,
      relative_return_pct: isFiniteNumber(r.relative_return)
        ? r.relative_return * 100
        : null,
    }));

    const last2YearMonthlyRows = (() => {
      if (!sortedPortfolioMacroRows.length) return [];

      const lastDate =
        sortedPortfolioMacroRows[sortedPortfolioMacroRows.length - 1]?.date;
      if (!lastDate) return [];

      const cutoff = new Date(`${lastDate}T00:00:00`);
      cutoff.setMonth(cutoff.getMonth() - 24);

      const filtered = sortedPortfolioMacroRows.filter((r) => {
        const d = new Date(`${r.date}T00:00:00`);
        return !Number.isNaN(d.getTime()) && d >= cutoff;
      });

      if (!filtered.length) return [];

      let portLevel = 1;
      let spyLevel = 1;

      const withLevels = filtered.map((r) => {
        if (isFiniteNumber(r.portfolio_return)) {
          portLevel *= 1 + r.portfolio_return;
        }
        if (isFiniteNumber(r.spy_return)) {
          spyLevel *= 1 + r.spy_return;
        }

        return {
          ...r,
          port_level: portLevel,
          spy_level: spyLevel,
        };
      });

      const monthEndMap = new Map();
      for (const r of withLevels) {
        const ym = r.date.slice(0, 7);
        monthEndMap.set(ym, r);
      }

      const monthEnds = Array.from(monthEndMap.values()).sort((a, b) =>
        a.date.localeCompare(b.date)
      );

      return monthEnds.map((r, idx) => {
        if (idx === 0) {
          return {
            date: r.date,
            macro_regime: r.macro_regime,
            portfolio_return_pct: 0,
            spy_return_pct: 0,
            relative_return_pct: 0,
          };
        }

        const prev = monthEnds[idx - 1];

        const portRet =
          isFiniteNumber(prev.port_level) &&
          isFiniteNumber(r.port_level) &&
          prev.port_level !== 0
            ? r.port_level / prev.port_level - 1
            : null;

        const spyRet =
          isFiniteNumber(prev.spy_level) &&
          isFiniteNumber(r.spy_level) &&
          prev.spy_level !== 0
            ? r.spy_level / prev.spy_level - 1
            : null;

        return {
          date: r.date,
          macro_regime: r.macro_regime,
          portfolio_return_pct: isFiniteNumber(portRet) ? portRet * 100 : null,
          spy_return_pct: isFiniteNumber(spyRet) ? spyRet * 100 : null,
          relative_return_pct:
            isFiniteNumber(portRet) && isFiniteNumber(spyRet)
              ? (portRet - spyRet) * 100
              : null,
        };
      });
    })();

    const dailyRowByDate = new Map(
      dailyParsed.rows.map((row) => [normalizeDateString(row.date), row.values])
    );

    const holdingMacroTypes = present.map((p, i) => {
      const rows = Array.isArray(macroJson)
        ? macroJson
            .map((m) => {
              const date = normalizeDateString(m?.date);
              const values = dailyRowByDate.get(date);
              if (!values) return null;

              const stockPct = values.get(p.ticker);
              const spyPct = values.get("SPY");

              if (!Number.isFinite(stockPct) || !Number.isFinite(spyPct)) {
                return null;
              }

              const stockReturn = stockPct / 100.0;
              const spyReturn = spyPct / 100.0;

              return {
                date,
                stock_return: stockReturn,
                spy_return: spyReturn,
                relative_return: stockReturn - spyReturn,
                inflation_score: Number.isFinite(Number(m?.inflation_score))
                  ? Number(m.inflation_score)
                  : null,
                growth_score: Number.isFinite(Number(m?.growth_score))
                  ? Number(m.growth_score)
                  : null,
                liquidity_score: Number.isFinite(Number(m?.liquidity_score))
                  ? Number(m.liquidity_score)
                  : null,
              };
            })
            .filter(Boolean)
            .map((r) => ({
              ...r,
              macro_regime: getMacroRegime(r),
            }))
        : [];

      const groups = new Map();

      for (const row of rows) {
        const key = row.macro_regime || "—";
        if (!groups.has(key)) {
          groups.set(key, {
            macro_regime: key,
            count_return: 0,
            sum_return: 0,
          });
        }
        const g = groups.get(key);
        if (isFiniteNumber(row.stock_return)) {
          g.sum_return += row.stock_return;
          g.count_return += 1;
        }
      }

      const summary = [...groups.values()]
        .map((g) => ({
          macro_regime: g.macro_regime,
          avg_daily_return:
            g.count_return > 0 ? g.sum_return / g.count_return : null,
        }))
        .filter((g) => isFiniteNumber(g.avg_daily_return))
        .sort((a, b) => b.avg_daily_return - a.avg_daily_return);

      const bestMacroType = summary.length
        ? summary[0].macro_regime
        : "Unclassified";

      return {
        ticker: p.ticker,
        weight: w[i],
        weightPct: w[i] * 100,
        bestMacroType,
      };
    });

    const stockTypeWeightsMap = new Map();

    for (const h of holdingMacroTypes) {
      const key = h.bestMacroType || "Unclassified";
      if (!stockTypeWeightsMap.has(key)) {
        stockTypeWeightsMap.set(key, {
          name: key,
          value: 0,
          tickers: [],
        });
      }
      const entry = stockTypeWeightsMap.get(key);
      entry.value += h.weightPct;
      entry.tickers.push(h.ticker);
    }

    const stockTypePieData = [...stockTypeWeightsMap.values()]
      .map((x) => ({
        ...x,
        value: Number(x.value.toFixed(4)),
      }))
      .sort((a, b) => b.value - a.value);

    const avgReturnStagflation = getAverageReturnForRegimeFamily(
      portfolioMacroRows,
      "stagflation"
    );
    const avgReturnDeflationaryRecession = getAverageReturnForRegimeFamily(
      portfolioMacroRows,
      "deflationary recession"
    );
    const avgReturnDeflationaryGrowth = getAverageReturnForRegimeFamily(
      portfolioMacroRows,
      "deflationary growth"
    );
    const avgReturnInflationaryGrowth = getAverageReturnForRegimeFamily(
      portfolioMacroRows,
      "inflationary growth"
    );

    const macroRegimeReturns = [
      avgReturnStagflation,
      avgReturnDeflationaryRecession,
      avgReturnDeflationaryGrowth,
      avgReturnInflationaryGrowth,
    ];

    const macroExposureByTickerRaw = new Map();
    const macroScaleValuesForScores = [];

    for (const row of macroExposureJson || []) {
      const ticker = normalizeTicker(row?.ticker ?? row?.Ticker ?? row?.symbol ?? row?.Symbol);
      if (!ticker) continue;

      const inflationRaw = getMacroExposure(row, "inflation");
      const growthRaw = getMacroExposure(row, "growth");
      const liquidityRaw = getMacroExposure(row, "liquidity");

      macroExposureByTickerRaw.set(ticker, {
        inflationRaw,
        growthRaw,
        liquidityRaw,
      });

      if (Number.isFinite(inflationRaw)) macroScaleValuesForScores.push(inflationRaw);
      if (Number.isFinite(growthRaw)) macroScaleValuesForScores.push(growthRaw);
      if (Number.isFinite(liquidityRaw)) macroScaleValuesForScores.push(liquidityRaw);
    }

    const quadrantWeights = {
      inflationaryGrowth: 0,
      stagflation: 0,
      deflationaryGrowth: 0,
      deflationaryRecession: 0,
    };

    for (let i = 0; i < present.length; i++) {
      const macro = macroExposureByTickerRaw.get(present[i].ticker);
      if (!macro) continue;

      const inflation = normalizeExposureForChart(
        macro.inflationRaw,
        macroScaleValuesForScores
      );
      const growth = normalizeExposureForChart(macro.growthRaw, macroScaleValuesForScores);

      if (!Number.isFinite(inflation) || !Number.isFinite(growth)) continue;

      if (inflation >= 0 && growth >= 0) quadrantWeights.inflationaryGrowth += w[i];
      else if (inflation >= 0 && growth < 0) quadrantWeights.stagflation += w[i];
      else if (inflation < 0 && growth >= 0) quadrantWeights.deflationaryGrowth += w[i];
      else quadrantWeights.deflationaryRecession += w[i];
    }

    const diversificationScore = computeEvennessScore(Object.values(quadrantWeights));

    const valueScorePieces = [];
    for (let i = 0; i < present.length; i++) {
      const valuation = stockScreenerValueMap.get(present[i].ticker);
      if (!valuation) continue;

      const scores = [
        scoreLowerMetricBetter(valuation.pb, 1, 10),
        scoreLowerMetricBetter(valuation.peg, 0.5, 3),
        scoreLowerMetricBetter(valuation.forwardPE, 8, 40),
      ].filter((x) => Number.isFinite(x));

      if (!scores.length) continue;

      valueScorePieces.push({
        weight: w[i],
        score: scores.reduce((sum, x) => sum + x, 0) / scores.length,
        pb: valuation.pb,
        peg: valuation.peg,
        forwardPE: valuation.forwardPE,
      });
    }

    const valueScoreWeightSum = valueScorePieces.reduce((sum, x) => sum + x.weight, 0);
    const valueScore =
      valueScoreWeightSum > 0
        ? valueScorePieces.reduce((sum, x) => sum + x.weight * x.score, 0) /
          valueScoreWeightSum
        : NaN;

    const weightedPB =
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.pb) ? x.weight * x.pb : 0),
        0
      ) /
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.pb) ? x.weight : 0),
        0
      );

    const weightedPEG =
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.peg) ? x.weight * x.peg : 0),
        0
      ) /
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.peg) ? x.weight : 0),
        0
      );

    const weightedForwardPE =
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.forwardPE) ? x.weight * x.forwardPE : 0),
        0
      ) /
      valueScorePieces.reduce(
        (sum, x) => sum + (Number.isFinite(x.forwardPE) ? x.weight : 0),
        0
      );

    const macroBalanceScore = computeMacroBalanceScore(macroRegimeReturns);
    const macroBalanceDownsideScore =
      computeMacroBalanceDownsideScore(macroRegimeReturns);

    const effectiveBetsScore = computeEffectiveBetsScore(neffCorr, n);

    const portfolioScore = computePortfolioScore({
      macroBalanceScore,
      macroBalanceDownsideScore,
      defensiveScore50: defensivenessScore50,
      diversificationScore,
      effectiveBetsScore,
      valueScore,
    });

    const buildMacroPie = (predicateFn) => {
      const candidates = portfolioMacroRows.filter(
        (r) => isFiniteNumber(r.portfolio_return) && predicateFn(r.portfolio_return)
      );

      const buckets = new Map([
        ["Inflationary Growth", 0],
        ["Stagflation", 0],
        ["Deflationary Growth", 0],
        ["Deflationary Recession", 0],
      ]);

      for (const row of candidates) {
        const collapsed = collapseMacroRegime(row.macro_regime);
        if (buckets.has(collapsed)) {
          buckets.set(collapsed, buckets.get(collapsed) + 1);
        }
      }

      const total = Array.from(buckets.values()).reduce((a, b) => a + b, 0);

      return Array.from(buckets.entries())
        .map(([name, days]) => ({
          name,
          days,
          value: total > 0 ? (days / total) * 100 : 0,
        }))
        .filter((x) => x.days > 0)
        .sort((a, b) => b.value - a.value);
    };

    const strongDaysMacroPieData = buildMacroPie((ret) => ret > 0.005);
    const badDaysMacroPieData = buildMacroPie((ret) => ret < -0.005);

    return {
      ok: true,
      missing,
      missingReasons,
      usedCount: n,
      overlapDays: portfolioSeries.length,
      spyDownDays: downDays.length,

      rhoBar,
      neffCorr,
      diversificationScore,
      effectiveBetsDiversificationScore,
      macroQuadrantWeights: quadrantWeights,
      valueScore,
      weightedPB,
      weightedPEG,
      weightedForwardPE,
      nanPairs,

      volP,
      dailyMove,
      wAvgVol,
      divRatio,
      portReturn,
      sharpe,

      betaSpy,
      corrSpy,
      downsideCapture,
      upWhenSpyDownRate,
      upWhenSpyDown15Rate,
      relativeVolToSpy,
      defensiveScore50: defensivenessScore50,
      volatilityScore50,
      portfolioRSI14,

      portfolioDividendYield,
      annualDividendIncome,

      sectorWeights,
      normalizedHoldings,
      riskTable,
      treemapData,

      portfolioMacroRows,
      portfolioMacroSummaryRows,
      portfolioRadarData,
      bestPortfolioMacroType,
      last30PortfolioRows,
      last2YearMonthlyRows,

      holdingMacroTypes,
      stockTypePieData,

      avgReturnStagflation,
      avgReturnDeflationaryRecession,
      avgReturnDeflationaryGrowth,
      avgReturnInflationaryGrowth,
      macroBalanceScore,
      macroBalanceDownsideScore,
      effectiveBetsScore,
      portfolioScore,
      strongDaysMacroPieData,
      badDaysMacroPieData,
    };
  }, [
    holdings,
    corrParsed,
    volAnnualMap,
    expAnnualMap,
    dailyParsed,
    dividendYieldMap,
    sectorMap,
    portfolioValueNum,
    macroJson,
    macroExposureJson,
    stockScreenerValueMap,
  ]);
if (loading) {
    return (
      <div style={{ padding: 16 }}>
        Loading correlation + vol + return + daily + dividend + sector + macro + exposure + valuation data…
      </div>
    );
  }

  if (err) {
    return <div style={{ padding: 16, color: "crimson" }}>Error: {err}</div>;
  }

  if (isMobile) {
    return (
      <MobilePortfolioDiversification
        input={input}
        setInput={setInput}
        portfolioValue={portfolioValue}
        setPortfolioValue={setPortfolioValue}
        result={result}
        macroExposureRows={macroExposureJson}
        isMobile={isMobile}
      />
    );
  }

  return (
    <div style={{ padding: isMobile ? 8 : 16 }}>
      <div
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #111827 35%, #1e293b 100%)",
          borderRadius: 24,
          padding: isMobile ? 16 : 28,
          marginBottom: isMobile ? 14 : 24,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(15,23,42,0.18)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "rgba(59,130,246,0.18)",
            filter: "blur(40px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -80,
            left: -80,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(168,85,247,0.14)",
            filter: "blur(40px)",
          }}
        />

        <div style={{ position: "relative", zIndex: 2 }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              flexWrap: "wrap",
              gap: 20,
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  fontSize: isMobile ? 10 : 12,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.65)",
                  fontWeight: 800,
                  marginBottom: 8,
                }}
              >
              </div>



{/*              <div
                style={{
                  fontSize: 36,
                  fontWeight: 1000,
                  fontstyle:"Calibri",
                  color: "#ffffff",
                  lineHeight: 1,
                  marginBottom: 14,
                }}
              >
                Portfolio Builder - by Majorah
              </div>
*/}

                          <div style={eyebrow}>by Majorah Intelligence</div>
            <h1 style={{ ...title, fontSize: isMobile ? 24 : title.fontSize }}>Portfolio Builder</h1>

              <div
                style={{
                  color: "rgba(255,255,255,0.72)",
                  fontSize: isMobile ? 13 : 15,
                  lineHeight: 1.6,
                }}
              >
              </div>

              <div
                style={{
                  color: "rgba(255,255,255,0.48)",
                  fontSize: isMobile ? 10 : 12,
                  marginTop: 12,
                }}
              >
                {volsJson?.as_of ? `Vol Data: ${volsJson.as_of}` : ""}
                {expJson?.as_of ? ` • Return Data: ${expJson.as_of}` : ""}
                {divJson?.as_of ? ` • Dividend Data: ${divJson.as_of}` : ""}
                {sectorJson?.as_of ? ` • Sector Data: ${sectorJson.as_of}` : ""}
                {` • Risk Free Rate: ${(RISK_FREE_RATE * 100).toFixed(2)}%`}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2,minmax(140px,1fr))",
                gap: 12,
                minWidth: isMobile ? 0 : 340,
              }}
            >
{/*              {[
                {
                  label: "Portfolio Score",
                  value:
                    result?.ok && isFiniteNumber(result?.portfolioScore)
                      ? Math.round(result.portfolioScore)
                      : "—",
                },
                {
                  label: "Diversification",
                  value:
                    result?.ok && isFiniteNumber(result?.diversificationScore)
                      ? Math.round(result.diversificationScore)
                      : "—",
                },
                {
                  label: "Defensive Score",
                  value:
                    result?.ok && isFiniteNumber(result?.defensiveScore50)
                      ? Math.round(result.defensiveScore50)
                      : "—",
                },
                {
                  label: "Holdings",
                  value: result?.ok ? result.usedCount : holdings.length,
                },
              ].map((card) => (
                <div
                  key={card.label}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 16,
                    padding: 14,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.55)",
                      marginBottom: 6,
                      fontWeight: 700,
                    }}
                  >
                    {card.label}
                  </div>

                  <div
                    style={{
                      fontSize: 30,
                      fontWeight: 1000,
                      color: "#ffffff",
                    }}
                  >
                    {card.value}
                  </div>
                </div>
              ))}
*/}            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(320px, 420px) 1fr",
          gap: 14,
          alignItems: "start",
        }}
      >
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Holdings</div>
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
            One per line:{" "}
            <span style={{ fontFamily: "monospace" }}>TICKER weight</span>{" "}
            (weights can be %, don’t need to sum to 100)
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={10}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              outline: "none",
              resize: "vertical",
            }}
          />

          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>
              Portfolio value
            </div>
            <input
              value={portfolioValue}
              onChange={(e) => setPortfolioValue(e.target.value)}
              placeholder="100000"
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontSize: 14,
                outline: "none",
              }}
            />
            <div style={{ marginTop: 6, fontSize: isMobile ? 10 : 12, opacity: 0.7 }}>
              Used to estimate annual dividend income.
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: isMobile ? 10 : 12, opacity: 0.75 }}>
            Sources:
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
            <div style={{ fontFamily: "monospace" }}>
            </div>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Results</div>

          {!result.ok ? (
            <div style={{ color: "crimson" }}>{result.reason}</div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 12,
                }}
              >
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(6, minmax(0, 1fr))",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <StatCard
                  label="Portfolio score"
                  value={
                    Number.isFinite(result.portfolioScore)
                      ? result.portfolioScore.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • structural risk/reward quality"
                  background="#eefaf3"
                />

                <StatCard
                  label="Number of holdings"
                  value={String(result.usedCount)}
                  sublabel="Tickers used in final calculation"
                  background="#f8fafc"
                />

                <StatCard
                  label="Volatility score"
                  value={
                    Number.isFinite(result.volatilityScore50)
                      ? result.volatilityScore50.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • high volatility = higher"
                  background="#f8fafc"
                />

                <StatCard
                  label="Defensiveness score"
                  value={
                    Number.isFinite(result.defensiveScore50)
                      ? result.defensiveScore50.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • lower beta = higher"
                  background="#f8fafc"
                />

                <StatCard
                  label="Diversity score"
                  value={
                    Number.isFinite(result.diversificationScore)
                      ? result.diversificationScore.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • equal 4-quadrant balance"
                  background="#f8fafc"
                />

                <StatCard
                  label="Value score"
                  value={
                    Number.isFinite(result.valueScore)
                      ? result.valueScore.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • low P/B, PEG, Fwd P/E"
                  background="#f8fafc"
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #f1f5f9",
                  borderRadius: 12,
                  padding: 12,
                  background: "#ffffff",
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Holdings & Macro Upside/Downside View
                </div>
                <div style={{ fontSize: isMobile ? 10 : 12, opacity: 0.72, marginBottom: 10 }}>
                  Left: holdings treemap. Middle: when portfolio is above +0.5%,
                  which macro regime family it tends to happen in. Right: when
                  portfolio is below -0.5%, which macro regime family it tends
                  to happen in.
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "minmax(260px, 320px) minmax(340px, 420px) minmax(340px, 420px)",
                    gap: 16,
                    alignItems: "start",
                    justifyContent: "start",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      padding: 8,
                      background: "#fff",
                      width: "100%",
                      maxWidth: isMobile ? "100%" : 320,
                      aspectRatio: isMobile ? "auto" : "1 / 1",
                      minHeight: isMobile ? 220 : 260,
                      overflow: "hidden",
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <Treemap
                        data={result.treemapData}
                        dataKey="size"
                        nameKey="name"
                        stroke="#ffffff"
                        content={<TreemapNode />}
                        aspectRatio={1}
                      >
                        <Tooltip
                          formatter={(value, _name, entry) => [
                            `${Number(value).toFixed(2)}%`,
                            entry?.payload?.name || "Weight",
                          ]}
                        />
                      </Treemap>
                    </ResponsiveContainer>
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      padding: 8,
                      background: "#fff",
                      width: "100%",
                      minHeight: isMobile ? 220 : 260,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 13,
                        padding: "4px 6px 0 6px",
                        color: "#000",
                      }}
                    >
                      Portfolio Up &gt; 0.5% Days by Macro Regime
                    </div>
                    <div style={{ width: "100%", height: isMobile ? 230 : 290 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={result.strongDaysMacroPieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={isMobile ? 70 : 92}
                            innerRadius={isMobile ? 28 : 42}
                            paddingAngle={2}
                            labelLine={isMobile ? false : false}
                            label={isMobile ? false : MacroPieLabel}
                          >
                            {result.strongDaysMacroPieData.map((entry) => {
                              const c = getRegimeColors(entry.name);
                              return (
                                <Cell
                                  key={`strong-${entry.name}`}
                                  fill={c.bg}
                                  stroke={c.text}
                                  strokeWidth={1.5}
                                />
                              );
                            })}
                          </Pie>
                          <Tooltip
                            content={
                              <MacroPieTooltip title="Portfolio Up > 0.5% Days" />
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      padding: 8,
                      background: "#fff",
                      width: "100%",
                      minHeight: isMobile ? 220 : 260,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 13,
                        padding: "4px 6px 0 6px",
                        color: "#000",
                      }}
                    >
                      Portfolio Down &lt; -0.5% Days by Macro Regime
                    </div>
                    <div style={{ width: "100%", height: isMobile ? 230 : 290 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={result.badDaysMacroPieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={isMobile ? 70 : 92}
                            innerRadius={isMobile ? 28 : 42}
                            paddingAngle={2}
                            labelLine={isMobile ? false : false}
                            label={isMobile ? false : MacroPieLabel}
                          >
                            {result.badDaysMacroPieData.map((entry) => {
                              const c = getRegimeColors(entry.name);
                              return (
                                <Cell
                                  key={`bad-${entry.name}`}
                                  fill={c.bg}
                                  stroke={c.text}
                                  strokeWidth={1.5}
                                />
                              );
                            })}
                          </Pie>
                          <Tooltip
                            content={
                              <MacroPieTooltip title="Portfolio Down < -0.5% Days" />
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #f3f4f6",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fff",
                    marginTop: 16,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 14,
                      marginBottom: 10,
                    }}
                  >
                    Portfolio Summary Metrics
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 10,
                    }}
                  >
                    <MetricItem
                      label="Annual Dividend Yield"
                      value={fmtPct(result.portfolioDividendYield, 2)}
                    />
                    <MetricItem
                      label="Total Dividend Income / Year"
                      value={fmtCurrency(result.annualDividendIncome, 0)}
                    />
                    <MetricItem
                      label="% Up on SPY Down Days"
                      value={fmtPct(result.upWhenSpyDownRate, 1)}
                    />
                    <MetricItem
                      label="% Up on SPY Days Down ≥ 1.5%"
                      value={fmtPct(result.upWhenSpyDown15Rate, 1)}
                    />
                    <MetricItem
                      label="Total Volatility Ratio vs SPY"
                      value={fmtNum(result.relativeVolToSpy, 3)}
                    />
                    <MetricItem
                      label="Total Diversification Ratio vs SPY"
                      value={fmtNum(result.divRatio, 3)}
                    />
                    <MetricItem
                      label="Portfolio Correlation to SPY"
                      value={fmtRho(result.corrSpy, 3)}
                    />
                    <MetricItem
                      label="Portfolio RSI Total"
                      value={fmtNum(result.portfolioRSI14, 1)}
                    />
                    <MetricItem
                      label="Portfolio Score"
                      value={
                        isFiniteNumber(result.portfolioScore)
                          ? `${result.portfolioScore.toFixed(1)} / 100`
                          : "—"
                      }
                    />
                    <MetricItem
                      label="Effective Bets Score"
                      value={
                        isFiniteNumber(result.effectiveBetsScore)
                          ? `${result.effectiveBetsScore.toFixed(1)} / 100`
                          : "—"
                      }
                    />
                    <MetricItem
                      label="Value Score"
                      value={
                        isFiniteNumber(result.valueScore)
                          ? `${result.valueScore.toFixed(1)} / 100`
                          : "—"
                      }
                    />
                    <MetricItem
                      label="Macro Balance Score"
                      value={
                        isFiniteNumber(result.macroBalanceScore)
                          ? `${result.macroBalanceScore.toFixed(1)} / 100`
                          : "—"
                      }
                    />
                    <MetricItem
                      label="Macro Balance + Downside Score"
                      value={
                        isFiniteNumber(result.macroBalanceDownsideScore)
                          ? `${result.macroBalanceDownsideScore.toFixed(1)} / 100`
                          : "—"
                      }
                    />
                    <MetricItem
                      label="Avg Return on Macro Stagflation Days"
                      value={fmtSignedPct(result.avgReturnStagflation, 2)}
                      valueStyle={getReturnStyle(result.avgReturnStagflation)}
                    />
                    <MetricItem
                      label="Avg Return on Macro Deflationary Recession Days"
                      value={fmtSignedPct(
                        result.avgReturnDeflationaryRecession,
                        2
                      )}
                      valueStyle={getReturnStyle(
                        result.avgReturnDeflationaryRecession
                      )}
                    />
                    <MetricItem
                      label="Avg Return on Deflationary Growth Days"
                      value={fmtSignedPct(result.avgReturnDeflationaryGrowth, 2)}
                      valueStyle={getReturnStyle(
                        result.avgReturnDeflationaryGrowth
                      )}
                    />
                    <MetricItem
                      label="Avg Return on Inflationary Growth Days"
                      value={fmtSignedPct(result.avgReturnInflationaryGrowth, 2)}
                      valueStyle={getReturnStyle(
                        result.avgReturnInflationaryGrowth
                      )}
                    />
                  </div>
                </div>

                <PortfolioMacroMap
                  holdings={result.normalizedHoldings}
                  macroExposureRows={macroExposureJson}
                  isMobile={isMobile}
                />

                <PortfolioMacroStripChart
                  rows={result.last30PortfolioRows}
                  currentPortfolioValue={
                    Number.isFinite(portfolioValueNum) && portfolioValueNum > 0
                      ? portfolioValueNum
                      : 100000
                  }
                  title="Last 30 Days Portfolio Value with Macro Regime Background"
                  subtitle="Background color shows the macro regime for each day. Black line = reconstructed portfolio value. Dashed gray line = SPY value, normalized to the same starting value as the portfolio on the first chart date."
                  isMonthly={false}
                  isMobile={isMobile}
                />

                <PortfolioMacroStripChart
                  rows={result.last2YearMonthlyRows}
                  currentPortfolioValue={
                    Number.isFinite(portfolioValueNum) && portfolioValueNum > 0
                      ? portfolioValueNum
                      : 100000
                  }
                  title="Last 2 Years Portfolio Value (Monthly) with Macro Regime Background"
                  subtitle="Each point is the month-end snapshot using compounded month-to-month returns. Black line = reconstructed portfolio value. Dashed gray line = SPY value, normalized to the same starting value as the portfolio on the first chart month."
                  isMonthly={true}
                  isMobile={isMobile}
                />
              </div>

              <div
                style={{
                  marginTop: 14,
                  border: "1px solid #eee",
                  borderRadius: 14,
                  background: "#fff",
                  padding: 12,
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 10 }}>
                  Portfolio Macro Profile
                </div>

                <div
                  style={{
                    marginBottom: 12,
                    display: "inline-block",
                    padding: "6px 10px",
                    borderRadius: 9999,
                    background: "#f3f4f6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Portfolio Type: {result.bestPortfolioMacroType}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "minmax(340px, 1fr) minmax(420px, 1fr)",
                    gap: 16,
                    alignItems: "start",
                  }}
                >
                  <div>
                    {result.portfolioRadarData.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No portfolio macro radar data available.
                      </div>
                    ) : (
                      <div style={{ width: "100%", height: isMobile ? 260 : 340 }}>
                        <ResponsiveContainer>
                          <RadarChart data={result.portfolioRadarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="metric" />
                            <PolarRadiusAxis
                              domain={[0, 100]}
                              tick={false}
                              axisLine={false}
                            />
                            <Radar
                              name="Portfolio Macro Performance"
                              dataKey="score"
                              stroke="#7c3aed"
                              fill="#7c3aed"
                              fillOpacity={0.28}
                            />
                            <Legend />
                            <Tooltip content={<RadarPerformanceTooltip />} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: isMobile ? 260 : 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Portfolio Macro Comparison Table
                    </div>

                    {result.portfolioMacroSummaryRows.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No portfolio macro comparison data available.
                      </div>
                    ) : (
                      <div style={{ overflowX: "hidden", maxHeight: 340 }}>
                        <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                 // minWidth: 0,
                  tableLayout: isMobile ? "fixed" : "auto",
                            minWidth: isMobile ? 0 : 760,
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              background: "#f9fafb",
                              position: "sticky",
                              top: 0,
                            }}
                          >
                            <tr>
                              <th style={tableThStyle}>Macro Environment</th>
                              <th style={tableThStyle}>Win Rate</th>
                              <th style={tableThStyle}>Avg Daily Return</th>
                              <th style={tableThStyle}>Avg Return vs SPY</th>
                              <th style={tableThStyle}>Total Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.portfolioMacroSummaryRows.map((r) => (
                              <tr key={r.macro_regime}>
                                <td style={tableTdStyle}>
                                  <span style={getRegimeStyle(r.macro_regime)}>
                                    {r.macro_regime}
                                  </span>
                                </td>
                                <td style={tableTdStyle}>
                                  {isFiniteNumber(r.win_rate)
                                    ? `${(r.win_rate * 100).toFixed(1)}%`
                                    : "—"}
                                </td>
                                <td
                                  style={{
                                    ...tableTdStyle,
                                    ...getReturnStyle(r.avg_daily_return),
                                  }}
                                >
                                  {fmtSignedPct(r.avg_daily_return, 2)}
                                </td>
                                <td
                                  style={{
                                    ...tableTdStyle,
                                    ...getReturnStyle(r.avg_return_vs_spy),
                                  }}
                                >
                                  {fmtSignedPct(r.avg_return_vs_spy, 2)}
                                </td>
                                <td style={tableTdStyle}>{r.total_days}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "minmax(340px, 1fr) minmax(420px, 1fr)",
                    gap: 16,
                    alignItems: "start",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: isMobile ? 260 : 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Portfolio Stock Type Breakdown
                    </div>

                    {result.stockTypePieData.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No stock-type breakdown available.
                      </div>
                    ) : (
                      <div style={{ width: "100%", height: isMobile ? 260 : 340 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={result.stockTypePieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={isMobile ? 70 : 78}
                              innerRadius={isMobile ? 28 : 40}
                              paddingAngle={2}
                              label={({ cx, cy, midAngle, outerRadius, name, value }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = outerRadius + 28;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                const percentText = `${Number(value).toFixed(1)}%`;

                                const words = String(name || "").split(" ");
                                const lines = [];
                                let currentLine = "";

                                for (const word of words) {
                                  const testLine = currentLine
                                    ? `${currentLine} ${word}`
                                    : word;
                                  if (testLine.length <= 16) {
                                    currentLine = testLine;
                                  } else {
                                    if (currentLine) lines.push(currentLine);
                                    currentLine = word;
                                  }
                                }

                                if (currentLine) lines.push(currentLine);
                                lines.push(percentText);

                                const lineHeight = 13;
                                const startDy = -((lines.length - 1) * lineHeight) / 2;

                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#000"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                    fontSize={11}
                                    fontWeight={600}
                                  >
                                    {lines.map((line, i) => (
                                      <tspan
                                        key={`${name}-${i}`}
                                        x={x}
                                        dy={i === 0 ? startDy : lineHeight}
                                      >
                                        {line}
                                      </tspan>
                                    ))}
                                  </text>
                                );
                              }}
                              labelLine={isMobile ? false : false}
                            >
                              {result.stockTypePieData.map((entry) => {
                                const c = getRegimeColors(entry.name);
                                return (
                                  <Cell
                                    key={`pie-${entry.name}`}
                                    fill={c.bg}
                                    stroke={c.text}
                                    strokeWidth={1.5}
                                  />
                                );
                              })}
                            </Pie>

                            <Tooltip
                              contentStyle={{ color: "#000" }}
                              itemStyle={{ color: "#000" }}
                              labelStyle={{ color: "#000" }}
                              content={<PieBreakdownTooltip />}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: isMobile ? 260 : 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Holding Stock Types
                    </div>

                    {result.holdingMacroTypes.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No holding type data available.
                      </div>
                    ) : (
                      <div style={{ overflowX: "hidden", maxHeight: 340 }}>
                        <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  //minWidth: 0,
                  tableLayout: isMobile ? "fixed" : "auto",
                            minWidth: isMobile ? 0 : 760,
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              background: "#f9fafb",
                              position: "sticky",
                              top: 0,
                            }}
                          >
                            <tr>
                              <th style={tableThStyle}>Ticker</th>
                              <th style={tableThStyle}>Weight</th>
                              <th style={tableThStyle}>Stock Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.holdingMacroTypes.map((r) => (
                              <tr key={r.ticker}>
                                <td style={tableTdStyle}>{r.ticker}</td>
                                <td style={tableTdStyle}>{r.weightPct.toFixed(2)}%</td>
                                <td style={tableTdStyle}>
                                  <span style={getRegimeStyle(r.bestMacroType)}>
                                    {r.bestMacroType}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    border: "1px solid #f3f4f6",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      padding: 10,
                      background: "#fafafa",
                      borderBottom: "1px solid #eee",
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                  >
                    Risk Table
                  </div>

                  <div style={{ overflowX: "hidden" }}>
                    <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  //minWidth: 0,
                  tableLayout: isMobile ? "fixed" : "auto",
                        minWidth: isMobile ? 0 : 760,
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead style={{ background: "#f9fafb" }}>
                        <tr>
                          <th style={tableThStyle}>Ticker</th>
                          <th style={tableThStyle}>Weight</th>
                          <th style={tableThStyle}>Sector</th>
                          <th style={tableThStyle}>Exp Return</th>
                          <th style={tableThStyle}>Vol</th>
                          <th style={tableThStyle}>Div Yield</th>
                          <th style={tableThStyle}>Variance Contrib</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.riskTable.map((r) => (
                          <tr key={r.ticker}>
                            <td style={tableTdStyle}>{r.ticker}</td>
                            <td style={tableTdStyle}>{r.weightPct.toFixed(2)}%</td>
                            <td style={tableTdStyle}>{r.sector}</td>
                            <td style={tableTdStyle}>
                              {Number(r.expAnnualPct).toFixed(2)}%
                            </td>
                            <td style={tableTdStyle}>
                              {Number(r.volAnnualPct).toFixed(2)}%
                            </td>
                            <td style={tableTdStyle}>
                              {isFiniteNumber(r.dividendYieldPct)
                                ? `${Number(r.dividendYieldPct).toFixed(2)}%`
                                : "—"}
                            </td>
                            <td style={tableTdStyle}>
                              {isFiniteNumber(r.pctVariance)
                                ? `${(r.pctVariance * 100).toFixed(2)}%`
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    border: "1px solid #f3f4f6",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      padding: 10,
                      background: "#fafafa",
                      borderBottom: "1px solid #eee",
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                  >
                    Sector Weights
                  </div>

                  <div style={{ overflowX: "hidden" }}>
                    <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                 // minWidth: 0,
                  tableLayout: isMobile ? "fixed" : "auto",
                        minWidth: isMobile ? 0 : 760,
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead style={{ background: "#f9fafb" }}>
                        <tr>
                          <th style={tableThStyle}>Sector</th>
                          <th style={tableThStyle}>Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.sectorWeights.map((r) => (
                          <tr key={r.sector}>
                            <td style={tableTdStyle}>{r.sector}</td>
                            <td style={tableTdStyle}>{r.weightPct.toFixed(2)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}